import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import {
  Users, DollarSign, AlertTriangle, Search, MoreVertical,
  Play, Pause, Ban, Gift, Trash2, ArrowUpRight,
  Building2, Phone, RefreshCw, UserPlus,
  Check, X, Mail, Calendar, Settings, Crown, Infinity, Wallet,
  Copy, Plus, Tag, GripVertical, ChevronUp, ChevronDown, ChevronLeft, Edit2,
  Mic, CreditCard, Webhook, ExternalLink, Info, Archive, RotateCcw, Sparkles, MessageCircle, Bug, Lightbulb, Star,
  Activity, Clock
} from 'lucide-react';
import ReactDOM from 'react-dom';
import TrialCodesPanel from './admin/TrialCodesPanel';
import PromotionsPanel from './admin/PromotionsPanel';
import AgencyPromoCell from './admin/AgencyPromoCell';
import CapacityPanel from '../components/admin/CapacityPanel';
import { BillingRatesPanel } from '../components/admin/BillingRatesPanel';
import { PaygTransitionPanel, EnterpriseLeadsPanel } from '../components/admin/PaygAdminPanels';
import { LayoutDashboard } from 'lucide-react';

const API_URL = window.location.origin;

/* ═══════════ LastActivityCell ═══════════
 * Renders "3h ago", "8d ago", "Never" with color-coded dot:
 *   🟢 <7d  (healthy)   🟡 7-30d  (at risk)   🔴 >30d  (churning)
 *   ⚫ never (never logged in — brand-new or truly dormant)
 *
 * Activity input: { last_active_at: ISO string, active_user_email, active_user_name }
 * or undefined when this workspace has never had an authenticated hit.
 */
const LastActivityCell = ({ activity, textSecondary }) => {
  if (!activity?.last_active_at) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs ${textSecondary}`}>
        <span className="w-2 h-2 rounded-full bg-gray-500/50" />
        <span className="opacity-70">Never</span>
      </span>
    );
  }
  const parsed = new Date(activity.last_active_at);
  const now = Date.now();
  const diffMs = now - parsed.getTime();
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  let label;
  if (minutes < 2) label = 'Just now';
  else if (minutes < 60) label = `${minutes}m ago`;
  else if (hours < 24) label = `${hours}h ago`;
  else label = `${days}d ago`;

  const colorClass = days >= 30 ? 'bg-red-400' : days >= 7 ? 'bg-yellow-400' : 'bg-emerald-400';
  const who = activity.active_user_email || activity.active_user_name || '';
  const title = who ? `Last active: ${label} · ${who}` : `Last active: ${label}`;

  return (
    <span className="inline-flex items-center gap-1.5 text-xs" title={title}>
      <span className={`w-2 h-2 rounded-full ${colorClass}`} />
      <span>{label}</span>
    </span>
  );
};

// Improved Portal component for dropdown menus - always visible
const DropdownPortal = ({ children, isOpen, anchorRef, onClose }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const menuWidth = 220;
      const menuHeight = 320;
      
      // Calculate position - prefer above if near bottom
      let top = rect.bottom + 8;
      let left = rect.right - menuWidth;
      
      // If menu would go below viewport, show above
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 8;
      }
      
      // Ensure left doesn't go negative
      if (left < 16) left = 16;
      
      setPosition({ top, left });
    }
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.target) &&
            anchorRef?.current && !anchorRef.current.contains(e.target)) {
          onClose();
        }
      };
      const handleScroll = () => onClose();
      
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, anchorRef, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="fixed z-[9999] animate-in fade-in-0 zoom-in-95 duration-100"
      style={{ top: position.top, left: position.left }}
    >
      {children}
    </div>,
    document.body
  );
};

// ─── Unlimited Cost Hover Pill ───
// Shows the `∞ Unlimited` pill + a portal-rendered tooltip revealing the real
// 30-day at-cost burn. Portaled to body so no parent overflow (scroll containers,
// table rows, modals) can clip it. Lazily fetches on first hover.
const UnlimitedCostPill = ({ api, workspaceId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const ensureLoaded = async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/agencies/${workspaceId}/monthly-cost`);
      setData(res.data);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const updateCoords = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 8, left: r.left });
  };

  const handleEnter = () => {
    updateCoords();
    setHovered(true);
    ensureLoaded();
  };

  useEffect(() => {
    if (!hovered) return;
    const onScroll = () => updateCoords();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [hovered]);

  return (
    <>
      <span
        ref={triggerRef}
        className="relative inline-block"
        onMouseEnter={handleEnter}
        onMouseLeave={() => setHovered(false)}
      >
        <span
          className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-teal-400/15 text-teal-300 border border-teal-400/30 inline-flex items-center gap-1 cursor-help"
          data-testid={`unlimited-balance-${workspaceId}`}
        >
          <Infinity size={10} /> Unlimited
        </span>
      </span>
      {hovered && ReactDOM.createPortal(
        <div
          className="fixed z-[9999] w-72 rounded-xl border border-white/10 bg-[#0d0f13] shadow-[0_20px_50px_-10px_rgba(0,0,0,0.8)] backdrop-blur-md p-3.5 pointer-events-none"
          style={{ top: coords.top, left: coords.left }}
          data-testid={`unlimited-cost-tooltip-${workspaceId}`}
        >
          <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-teal-300/80 mb-1.5">
            30-day platform burn
          </div>
          {loading || !data ? (
            <div className="text-xs text-slate-400">Loading…</div>
          ) : data.error ? (
            <div className="text-xs text-rose-400">Failed to load</div>
          ) : (
            <>
              <div className="flex items-baseline gap-1.5 mb-2.5">
                <span className="text-2xl font-bold text-white tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  ~${Number(data.total_usd || 0).toFixed(2)}
                </span>
                <span className="text-[11px] text-slate-500">at cost · 30 days</span>
              </div>
              <div className="space-y-1 text-[11px]">
                {[
                  { key: 'voice', label: 'Voice', dot: '#10b981' },
                  { key: 'chat',  label: 'Chat',  dot: '#3b82f6' },
                  { key: 'kb',    label: 'KB',    dot: '#a855f7' },
                  { key: 'orb',   label: 'Test (orb)', dot: '#f59e0b' },
                ].map((row) => {
                  const b = (data.breakdown || {})[row.key] || { quantity: 0, cost: 0, unit: '' };
                  return (
                    <div key={row.key} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: row.dot }} />
                        <span className="text-slate-400">{row.label}</span>
                        <span className="text-slate-500 tabular-nums">· {b.quantity} {b.unit}</span>
                      </div>
                      <span className="text-white tabular-nums font-medium">${Number(b.cost || 0).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2.5 pt-2 border-t border-white/[0.06] text-[10px] text-slate-500">
                Usage tracked for cost reporting · no deduction on balance
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
};



// Self-Heal Maintenance UI removed (2026-02-26). The backfill loops
// (utils/self_healing.py) still run automatically every 15-30 min for
// every workspace — no admin button needed. The manual sweep endpoint
// `/api/admin/self-heal/run-retroactive-sweep` is preserved if anyone
// needs to fire it via curl.

const ElevenLabsHealthCard = ({ api, cardClass, textPrimary, textSecondary, isDarkMode }) => {
  const [health, setHealth] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [resyncing, setResyncing] = React.useState(false);
  const [report, setReport] = React.useState(null);

  const loadHealth = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/elevenlabs/agents/health');
      setHealth(data);
    } catch (e) {
      setHealth({ error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const runResyncAll = async () => {
    if (!window.confirm('Re-sync ALL ElevenLabs agents now? This will re-create webhook tools and update agent configs on EL.')) return;
    setResyncing(true);
    setReport(null);
    try {
      const { data } = await api.post('/api/admin/elevenlabs/agents/resync-all');
      setReport(data);
      loadHealth();
    } catch (e) {
      setReport({ error: e?.response?.data?.detail || e.message });
    } finally {
      setResyncing(false);
    }
  };

  return (
    <div className={`${cardClass} rounded-2xl overflow-hidden`} data-testid="elevenlabs-health-card">
      <div className="p-6 border-b border-white/[0.06]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className={`text-lg font-semibold ${textPrimary}`}>ElevenLabs Agent Health</h3>
            <p className={`text-sm ${textSecondary} mt-1`}>
              Verify every voice agent on EL has its webhook tools (update_contact, schedule_callback, etc.) and re-sync if missing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadHealth}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-xs font-semibold border border-white/15 hover:border-teal-400/40 disabled:opacity-50"
              data-testid="el-health-check-btn"
            >
              {loading ? 'CHECKING…' : 'CHECK HEALTH'}
            </button>
            <button
              onClick={runResyncAll}
              disabled={resyncing}
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-teal-500/20 text-teal-400 border border-teal-400/40 hover:bg-teal-500/30 disabled:opacity-50"
              data-testid="el-resync-all-btn"
            >
              {resyncing ? 'RE-SYNCING…' : 'RE-SYNC ALL AGENTS'}
            </button>
          </div>
        </div>
      </div>

      {health && !health.error && (
        <div className="p-6 border-b border-white/[0.06]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <ELStat label="Total" value={health.summary.total_agents} textPrimary={textPrimary} textSecondary={textSecondary} />
            <ELStat label="On EL" value={health.summary.exists_on_el} good textPrimary={textPrimary} textSecondary={textSecondary} />
            <ELStat label="Missing on EL" value={health.summary.missing_on_el} bad={health.summary.missing_on_el > 0} textPrimary={textPrimary} textSecondary={textSecondary} />
            <ELStat label="Need update_contact resync" value={health.summary.needs_resync_for_update_contact} bad={health.summary.needs_resync_for_update_contact > 0} textPrimary={textPrimary} textSecondary={textSecondary} />
          </div>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-white/10" data-testid="el-health-table">
            <table className="w-full text-xs">
              <thead className={`sticky top-0 ${isDarkMode ? 'bg-black/40' : 'bg-gray-100'} ${textSecondary}`}>
                <tr><th className="px-3 py-2 text-left">Agent</th><th>EL</th><th>uc tool</th><th>fields</th><th>tools</th></tr>
              </thead>
              <tbody className={textPrimary}>
                {health.agents.map(a => (
                  <tr key={a.agent_id} className="border-t border-white/5">
                    <td className="px-3 py-2">{a.name} <span className={textSecondary}>({a.agent_id})</span></td>
                    <td className="text-center">{a.exists_on_el ? '✓' : <span className="text-rose-400">{a.el_status_code || '×'}</span>}</td>
                    <td className="text-center">{a.has_update_contact_in_db ? (a.has_update_contact_tool ? '✓' : <span className="text-rose-400">×</span>) : '—'}</td>
                    <td className="text-center">{a.update_contact_field_count}</td>
                    <td className={`text-center text-[10px] ${textSecondary}`}>{(a.tool_names || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {health?.error && (
        <div className="p-6 text-xs text-rose-400">Error: {health.error}</div>
      )}

      {report && !report.error && (
        <div className="p-6">
          <div className="mb-3 flex items-center gap-4 text-sm">
            <span className="text-teal-400">✓ Synced: {report.summary.synced}</span>
            {report.summary.failed > 0 && <span className="text-rose-400">✗ Failed: {report.summary.failed}</span>}
            {report.summary.skipped > 0 && <span className={textSecondary}>Skipped: {report.summary.skipped}</span>}
          </div>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-white/10" data-testid="el-resync-report">
            <table className="w-full text-xs">
              <thead className={`sticky top-0 ${isDarkMode ? 'bg-black/40' : 'bg-gray-100'} ${textSecondary}`}>
                <tr><th className="px-3 py-2 text-left">Agent</th><th>Status</th><th>Tools</th><th>Outbound</th><th className="text-left">Error</th></tr>
              </thead>
              <tbody className={textPrimary}>
                {report.agents.map(a => (
                  <tr key={a.agent_id} className="border-t border-white/5">
                    <td className="px-3 py-2">{a.name} <span className={textSecondary}>({a.agent_id})</span></td>
                    <td className={`text-center ${a.status === 'synced' ? 'text-teal-400' : a.status === 'failed' ? 'text-rose-400' : textSecondary}`}>{a.status}</td>
                    <td className="text-center">{a.tool_count ?? '—'}</td>
                    <td className="text-center">
                      {a.outbound_synced === true ? <span className="text-teal-400">✓</span>
                        : a.outbound_synced === false ? <span className="text-rose-400">✗</span>
                        : '—'}
                    </td>
                    <td className={`px-2 text-[10px] ${textSecondary} max-w-md truncate`} title={a.error || ''}>{a.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {report?.error && (
        <div className="p-6 text-xs text-rose-400">Error: {report.error}</div>
      )}
    </div>
  );
};

const ELStat = ({ label, value, good, bad, textPrimary, textSecondary }) => (
  <div className={`p-3 rounded-lg border border-white/10 ${good ? 'bg-teal-500/10' : bad ? 'bg-rose-500/10' : ''}`}>
    <div className={`text-[10px] uppercase tracking-wider ${textSecondary}`}>{label}</div>
    <div className={`text-2xl font-bold mt-1 ${good ? 'text-teal-400' : bad ? 'text-rose-400' : textPrimary}`}>{value}</div>
  </div>
);



const AdminDashboard = () => {
  const { user, api } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data state
  const [stats, setStats] = useState(null);
  const [agencies, setAgencies] = useState([]);
  // Map `workspace_id` → { last_active_at, active_user_email, active_user_name }
  // Plus aggregate counters (active_last_7d, total_agencies) for the KPI card.
  const [agencyActivity, setAgencyActivity] = useState({ workspaces: {}, active_last_7d: 0, total_agencies: 0 });
  const [activity, setActivity] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [planConfig, setPlanConfig] = useState(null);
  const [billingRates, setBillingRates] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAgencyMenu, setShowAgencyMenu] = useState(null);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsAgency, setSettingsAgency] = useState(null);
  const [showGrantAccessModal, setShowGrantAccessModal] = useState(false);
  const [grantAccessAgency, setGrantAccessAgency] = useState(null);
  const [grantAccessEmail, setGrantAccessEmail] = useState('');
  const [grantAccessPermission, setGrantAccessPermission] = useState('viewer');
  const [isGrantingAccess, setIsGrantingAccess] = useState(false);
  const [grantAccessResult, setGrantAccessResult] = useState(null);
  const [changeEmailValue, setChangeEmailValue] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);
  const [changeEmailResult, setChangeEmailResult] = useState(null);
  const [showFreeMonthsModal, setShowFreeMonthsModal] = useState(false);
  const [showAgencyPricingModal, setShowAgencyPricingModal] = useState(false);
  const [pricingAgency, setPricingAgency] = useState(null);
  const [agencyPricingForm, setAgencyPricingForm] = useState({
    custom_voice_rate: '',
    custom_phone_fee: '',
    custom_sms_rate: '',
    custom_included_minutes: '',
    notes: ''
  });
  const [savingAgencyPricing, setSavingAgencyPricing] = useState(false);
  const [freeMonthsAgency, setFreeMonthsAgency] = useState(null);
  const [deleteConfirmAgency, setDeleteConfirmAgency] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [archivedAgencies, setArchivedAgencies] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);

  // Users management state
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersFilter, setUsersFilter] = useState('all');
  const [usersSearch, setUsersSearch] = useState('');
  
  // Webhook settings state
  const [webhookSettings, setWebhookSettings] = useState({ signup_webhook_url: '', signup_webhook_enabled: false });
  const [savingWebhook, setSavingWebhook] = useState(false);
  
  const menuAnchorRefs = useRef({});
  
  // Forms
  const [newTeamMember, setNewTeamMember] = useState({ name: '', email: '', role: 'admin' });
  const [newWorkspace, setNewWorkspace] = useState({
    name: '', email: '', plan: 'starter', isLifetime: false, lifetimeMinutes: 2000, free_months: 0, sendInvite: true, promo_id: ''
  });
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [activePromos, setActivePromos] = useState([]);
  // Beta invite state
  const [newBetaUser, setNewBetaUser] = useState({ name: '', person_name: '', email: '' });
  const [betaInvitePreview, setBetaInvitePreview] = useState(false);
  const [sendingBetaInvite, setSendingBetaInvite] = useState(false);
  const [betaProgramStatus, setBetaProgramStatus] = useState({ ended: false, total_beta_workspaces: 0 });
  const [togglingBetaProgram, setTogglingBetaProgram] = useState(false);
  const [freeMonthsData, setFreeMonthsData] = useState({ months: 1, resume_date: '' });
  const [settingsForm, setSettingsForm] = useState({ 
    name: '', plan: '', newPassword: '', isLifetime: false, 
    customMrr: '', useCustomMrr: false, subAccountLimit: 0,
    unlimitedCredits: false, rateTier: 'standard'
  });
  
  // Plan editing state
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPlanForm, setNewPlanForm] = useState({ name: '', monthly: 0, voiceRate: '0.21', minutes: 0, subAccounts: 1, color: '#14b8a6' });
  const [yearlyConfig, setYearlyConfig] = useState({ type: 'percentage', value: 15 }); // 15% off or fixed amount

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, agenciesRes, activityRes, teamRes, plansRes, billingRes, agencyActivityRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/agencies'),
        api.get('/api/admin/activity'),
        api.get('/api/admin/team'),
        api.get('/api/admin/plan-config').catch(() => ({ data: null })),
        api.get('/api/admin/pricing').catch(() => ({ data: null })),
        api.get('/api/admin/agencies/activity').catch(() => ({ data: { workspaces: {}, active_last_7d: 0, total_agencies: 0 } })),
      ]);
      
      setStats(statsRes.data);
      setAgencies(agenciesRes.data);
      setActivity(activityRes.data);
      setTeamMembers(teamRes.data);
      setAgencyActivity(agencyActivityRes.data || { workspaces: {}, active_last_7d: 0, total_agencies: 0 });
      if (plansRes.data) {
        setPlanConfig(plansRes.data);
        if (plansRes.data.yearlyConfig) {
          setYearlyConfig(plansRes.data.yearlyConfig);
        }
      }
      if (billingRes.data) {
        setBillingRates(billingRes.data);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); fetchBetaProgramStatus(); }, [fetchData]);

  // Fetch webhook settings when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings') {
      api.get('/api/admin/settings/webhooks').then(res => {
        setWebhookSettings(res.data);
      }).catch(() => {});
    }
    if (activeTab === 'archived') {
      fetchArchivedAgencies();
    }
    if (activeTab === 'create' || activeTab === 'agencies') {
      api.get('/api/admin/promotions').then(res => {
        setActivePromos((res.data?.promotions || []).filter(p => p.status === 'active'));
      }).catch(() => {});
    }
  }, [activeTab, api]);

  const saveWebhookSettings = async () => {
    setSavingWebhook(true);
    try {
      await api.put('/api/admin/settings/webhooks', webhookSettings);
      toast.success('Webhook settings saved');
    } catch (err) {
      toast.error('Failed to save webhook settings');
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleAgencyAction = async (agencyId, action, extra = {}) => {
    if (action === 'delete') {
      const agency = agencies.find(a => (a.workspace_id || a.id) === agencyId);
      setDeleteConfirmAgency({ id: agency?.workspace_id || agencyId, name: agency?.name || 'this agency' });
      setShowAgencyMenu(null);
      return;
    }
    if (action === 'remind') {
      try {
        const res = await api.post(`/api/admin/agencies/${agencyId}/send-reminder`);
        if (res.data?.success) {
          toast.success(`Reminder sent (${res.data?.type || 'email'})`);
        } else {
          toast.error('Email send failed — check backend logs');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Workspace not in grace period');
      }
      setShowAgencyMenu(null);
      return;
    }
    try {
      await api.post(`/api/admin/agencies/${agencyId}/action`, { action, ...extra });
      toast.success(`Action "${action}" completed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
    setShowAgencyMenu(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmAgency || deleteConfirmText !== 'DELETE') return;
    setIsDeleting(true);
    try {
      await api.delete(`/api/admin/agencies/${deleteConfirmAgency.id}`);
      toast.success(`"${deleteConfirmAgency.name}" has been archived`);
      setDeleteConfirmAgency(null);
      setDeleteConfirmText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Archive failed');
    }
    setIsDeleting(false);
  };

  // Permanent delete modal state (archived workspaces only)
  const [permanentDeleteAgency, setPermanentDeleteAgency] = useState(null);
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState('');
  const [permanentDeleting, setPermanentDeleting] = useState(false);

  // Feedback tab state (thread-grouped model)
  const [feedbackThreads, setFeedbackThreads] = useState([]);
  const [feedbackCounts, setFeedbackCounts] = useState({ total: 0, unread: 0, favorites: 0, completed: 0 });
  const [feedbackFilter, setFeedbackFilter] = useState('all'); // all | new | read | favorites | completed
  const [selectedThreadEmail, setSelectedThreadEmail] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const feedbackUnread = feedbackCounts.unread;

  const fetchFeedbackThreads = async (filter = feedbackFilter) => {
    setFeedbackLoading(true);
    try {
      const params = filter && filter !== 'all' ? `?filter=${filter}` : '';
      const res = await api.get(`/api/admin/feedback/threads${params}`);
      setFeedbackThreads(res.data?.threads || []);
      setFeedbackCounts({
        total: res.data?.total || 0,
        unread: res.data?.unread || 0,
        favorites: res.data?.favorites || 0,
        completed: res.data?.completed || 0,
      });
    } catch { /* ignore */ }
    finally { setFeedbackLoading(false); }
  };

  const fetchFeedbackUnread = async () => {
    try {
      const res = await api.get('/api/admin/feedback/unread-count');
      setFeedbackCounts((c) => ({
        ...c,
        unread: res.data?.unread || 0,
        favorites: res.data?.favorites || 0,
        completed: res.data?.completed || 0,
        total: res.data?.total || c.total,
      }));
    } catch { /* ignore */ }
  };

  const openThread = async (email) => {
    setSelectedThreadEmail(email);
    setThreadLoading(true);
    setThreadMessages([]);
    try {
      const res = await api.get(`/api/admin/feedback/by-user`, { params: { email } });
      const items = res.data?.items || [];
      setThreadMessages(items);
      // Auto-mark all unread in this thread as read
      const newOnes = items.filter(m => m.status === 'new');
      await Promise.all(newOnes.map(m => api.patch(`/api/admin/feedback/${m.feedback_id}/read`).catch(() => {})));
      if (newOnes.length > 0) {
        fetchFeedbackThreads(feedbackFilter);
        fetchFeedbackUnread();
      }
    } catch { /* ignore */ }
    finally { setThreadLoading(false); }
  };

  const toggleFavorite = async (feedbackId) => {
    try {
      const res = await api.patch(`/api/admin/feedback/${feedbackId}/favorite`);
      const newVal = !!res.data?.is_favorite;
      setThreadMessages((msgs) => msgs.map(m => m.feedback_id === feedbackId ? { ...m, is_favorite: newVal } : m));
      fetchFeedbackUnread();
    } catch { toast.error('Failed to toggle favorite'); }
  };

  const toggleComplete = async (feedbackId) => {
    try {
      const res = await api.patch(`/api/admin/feedback/${feedbackId}/complete`);
      const newVal = !!res.data?.is_completed;
      setThreadMessages((msgs) => msgs.map(m => m.feedback_id === feedbackId ? { ...m, is_completed: newVal, status: newVal ? 'read' : m.status } : m));
      fetchFeedbackUnread();
    } catch { toast.error('Failed to toggle completed'); }
  };

  useEffect(() => { fetchFeedbackUnread(); }, []);
  useEffect(() => { if (activeTab === 'feedback') fetchFeedbackThreads(feedbackFilter); }, [activeTab, feedbackFilter]);

  const fetchArchivedAgencies = async () => {
    setLoadingArchived(true);
    try {
      const res = await api.get('/api/admin/agencies?status_filter=archived');
      setArchivedAgencies(res.data);
    } catch (err) {
      toast.error('Failed to load archived workspaces');
    } finally {
      setLoadingArchived(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get('/api/admin/users', { params: { filter: usersFilter, search: usersSearch } });
      setUsersList(res.data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (u) => {
    const isOwner = u.category === 'owner';
    const confirmMsg = isOwner
      ? `${u.email} OWNS ${u.owned_workspaces.length} workspace(s). Deleting orphans them — continue?`
      : `Delete ${u.email}? This removes the user and any access grants.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.delete(`/api/admin/users/${u.user_id}`, { params: { force: isOwner } });
      toast.success(`Deleted ${u.email}`);
      fetchUsers();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  useEffect(() => { if (activeTab === 'users') fetchUsers(); /* eslint-disable-next-line */ }, [activeTab, usersFilter]);


  const handleRestoreAgency = async (agencyId, agencyName) => {
    try {
      await api.post(`/api/admin/agencies/${agencyId}/restore`);
      toast.success(`"${agencyName}" has been restored`);
      fetchArchivedAgencies();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteAgency) return;
    if (permanentDeleteConfirm.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setPermanentDeleting(true);
    try {
      const id = permanentDeleteAgency.workspace_id || permanentDeleteAgency.id;
      const res = await api.post(`/api/admin/agencies/${id}/permanent-delete`, {
        confirmation: permanentDeleteConfirm,
      });
      toast.success(res.data?.message || `"${permanentDeleteAgency.name}" permanently deleted`);
      setPermanentDeleteAgency(null);
      setPermanentDeleteConfirm('');
      fetchArchivedAgencies();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Permanent delete failed');
    } finally {
      setPermanentDeleting(false);
    }
  };

  const handleOpenFreeMonthsModal = (agency) => {
    setFreeMonthsAgency(agency);
    const resumeDate = new Date();
    resumeDate.setMonth(resumeDate.getMonth() + 1);
    setFreeMonthsData({ months: 1, resume_date: resumeDate.toISOString().split('T')[0] });
    setShowFreeMonthsModal(true);
    setShowAgencyMenu(null);
  };

  const handleGrantFreeMonths = async (e) => {
    e.preventDefault();
    if (!freeMonthsAgency) return;
    try {
      await api.post(`/api/admin/agencies/${freeMonthsAgency.id}/action`, {
        action: 'grant_free_months', months: freeMonthsData.months, resume_date: freeMonthsData.resume_date
      });
      toast.success(`Granted ${freeMonthsData.months} free month(s)`);
      setShowFreeMonthsModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to grant free months');
    }
  };

  // Agency custom pricing functions
  const handleOpenAgencyPricing = async (agency) => {
    setPricingAgency(agency);
    try {
      const res = await api.get(`/api/admin/agencies/${agency.workspace_id}/pricing`);
      const custom = res.data?.custom_pricing || {};
      setAgencyPricingForm({
        custom_voice_rate: custom.voice_rate || '',
        custom_phone_fee: custom.phone_fee || '',
        custom_sms_rate: custom.sms_rate || '',
        custom_included_minutes: custom.included_minutes || '',
        notes: custom.notes || ''
      });
    } catch (error) {
      setAgencyPricingForm({
        custom_voice_rate: '', custom_phone_fee: '', custom_sms_rate: '', custom_included_minutes: '', notes: ''
      });
    }
    setShowAgencyPricingModal(true);
    setShowAgencyMenu(null);
  };

  const handleSaveAgencyPricing = async (e) => {
    e.preventDefault();
    if (!pricingAgency) return;
    setSavingAgencyPricing(true);
    try {
      await api.put(`/api/admin/agencies/${pricingAgency.workspace_id}/pricing`, {
        custom_voice_rate: agencyPricingForm.custom_voice_rate ? parseFloat(agencyPricingForm.custom_voice_rate) : null,
        custom_phone_fee: agencyPricingForm.custom_phone_fee ? parseFloat(agencyPricingForm.custom_phone_fee) : null,
        custom_sms_rate: agencyPricingForm.custom_sms_rate ? parseFloat(agencyPricingForm.custom_sms_rate) : null,
        custom_included_minutes: agencyPricingForm.custom_included_minutes ? parseInt(agencyPricingForm.custom_included_minutes) : null,
        notes: agencyPricingForm.notes || null
      });
      toast.success('Agency pricing updated');
      setShowAgencyPricingModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingAgencyPricing(false);
    }
  };

  const handleResetAgencyPricing = async () => {
    if (!pricingAgency) return;
    if (!window.confirm('Reset this agency to default plan pricing?')) return;
    try {
      await api.delete(`/api/admin/agencies/${pricingAgency.workspace_id}/pricing`);
      toast.success('Pricing reset to defaults');
      setShowAgencyPricingModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to reset pricing');
    }
  };

  const handleUpdateBillingRates = async (newRates) => {
    try {
      await api.put('/api/admin/pricing', newRates);
      toast.success('Billing rates updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update billing rates');
    }
  };

  const handleAddTeamMember = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/team', newTeamMember);
      toast.success('Team member added');
      setShowAddTeamModal(false);
      setNewTeamMember({ name: '', email: '', role: 'admin' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleRemoveTeamMember = async (userId) => {
    if (confirmDeleteId !== userId) {
      setConfirmDeleteId(userId);
      return;
    }
    try {
      await api.delete(`/api/admin/team/${userId}`);
      toast.success('Team member removed');
      setConfirmDeleteId(null);
      fetchData();
    } catch (error) {
      console.error('Delete team member error:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    setCreatingWorkspace(true);
    try {
      await api.post('/api/admin/workspaces', {
        ...newWorkspace,
        plan: newWorkspace.isLifetime ? 'lifetime' : newWorkspace.plan,
        lifetime_base_plan: newWorkspace.isLifetime ? newWorkspace.plan : undefined,
        lifetime_minutes: newWorkspace.isLifetime ? newWorkspace.lifetimeMinutes : undefined,
        free_months: newWorkspace.isLifetime ? 0 : Number(newWorkspace.free_months || 0),
        isLifetime: newWorkspace.isLifetime,
        promo_id: newWorkspace.promo_id || undefined,
      });
      toast.success('Workspace created! Invite email sent.');
      setNewWorkspace({ name: '', email: '', plan: 'starter', isLifetime: false, lifetimeMinutes: 2000, free_months: 0, sendInvite: true, promo_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const fetchBetaProgramStatus = async () => {
    try {
      const res = await api.get('/api/admin/beta/program-status');
      setBetaProgramStatus(res.data || {});
    } catch { /* ignore */ }
  };

  const handleBetaInviteSubmit = async () => {
    if (!newBetaUser.email || !newBetaUser.name || !newBetaUser.person_name) {
      toast.error('Please enter business name, person\u2019s name, and email');
      return;
    }
    setSendingBetaInvite(true);
    try {
      const res = await api.post('/api/admin/beta/invite', newBetaUser);
      const wh = res.data?.webhook || {};
      if (wh.fired) {
        toast.success(`Beta invite sent to ${newBetaUser.email} · Webhook fired (HTTP ${wh.status_code})`);
      } else if (wh.reason && /not configured|disabled/i.test(wh.reason)) {
        toast.success(`Beta invite sent to ${newBetaUser.email}`, {
          description: 'Webhook skipped — enable it in Settings → New User Webhook to sync with LeadConnector.'
        });
      } else if (wh.reason) {
        toast.warning(`Beta invite sent, but webhook failed: ${wh.reason}`);
      } else {
        toast.success(`Beta invite sent to ${newBetaUser.email}`);
      }
      setNewBetaUser({ name: '', person_name: '', email: '' });
      setBetaInvitePreview(false);
      fetchData();
      fetchBetaProgramStatus();
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send beta invite');
    } finally {
      setSendingBetaInvite(false);
    }
  };

  const handleToggleBetaProgram = async (end) => {
    if (end && !window.confirm('End the Beta Program for ALL beta users? They will see a mandatory choose-your-path modal on next login, and LIFETIME pricing jumps from $3,000 to $5,000.')) return;
    if (!end && !window.confirm('Re-open the Beta Program? Lifetime pricing will return to $3,000 during active beta.')) return;
    setTogglingBetaProgram(true);
    try {
      await api.post('/api/admin/beta/end-program', { end });
      toast.success(end ? 'Beta program ended' : 'Beta program re-opened');
      fetchBetaProgramStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle beta program');
    } finally {
      setTogglingBetaProgram(false);
    }
  };

  // Schedule the beta program to auto-end at a specific datetime.
  // Server validates: must be ≥5min in the future. Pass null to clear.
  // The auto-end loop in server.py polls every 60s and fires the same
  // `_trigger_program_end()` path the manual button uses.
  const handleSetBetaEndDate = async (isoDate) => {
    try {
      const res = await api.post('/api/admin/beta/set-end-date', { end_date: isoDate });
      if (isoDate === null) {
        toast.success('Countdown cleared');
      } else {
        const days = Math.floor((res.data?.countdown_seconds || 0) / 86400);
        toast.success(`Countdown set — beta ends in ~${days} day${days === 1 ? '' : 's'}`);
      }
      fetchBetaProgramStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set countdown');
    }
  };

  const handleOpenSettings = (agency) => {
    setSettingsAgency(agency);
    const planData = planConfig?.plans?.find(p => p.id === (agency.base_plan || agency.plan));
    const standardMrr = planData?.monthly || 0;
    const hasCustomMrr = agency.custom_mrr !== undefined && agency.custom_mrr !== null && agency.mrr !== standardMrr;
    
    setSettingsForm({
      name: agency.name || '',
      plan: agency.base_plan || (isLifetimeAgency(agency) ? (agency.lifetime_base_plan || 'pro') : agency.plan) || 'starter',
      newPassword: '',
      isLifetime: isLifetimeAgency(agency),
      customMrr: hasCustomMrr ? agency.mrr : '',
      useCustomMrr: hasCustomMrr,
      subAccountLimit: agency.sub_account_limit || planData?.subAccounts || 3,
      unlimitedCredits: agency.unlimited_credits || false,
      rateTier: agency.rate_tier || 'standard'
    });
    setShowSettingsModal(true);
    setShowAgencyMenu(null);
    setChangeEmailValue('');
    setChangeEmailResult(null);
  };

  const handleOpenGrantAccess = (agency) => {
    setGrantAccessAgency(agency);
    setGrantAccessEmail('');
    setGrantAccessPermission('viewer');
    setGrantAccessResult(null);
    setShowGrantAccessModal(true);
    setShowAgencyMenu(null);
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!grantAccessAgency || !grantAccessEmail) return;
    setIsGrantingAccess(true);
    setGrantAccessResult(null);
    try {
      const workspaceId = grantAccessAgency.workspace_id || grantAccessAgency.id;
      const response = await api.post('/api/access/grant', {
        email: grantAccessEmail,
        workspace_id: workspaceId,
        permission_level: grantAccessPermission
      });
      setGrantAccessResult(response.data);
      setGrantAccessEmail('');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setIsGrantingAccess(false);
    }
  };


  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!settingsAgency) return;
    
    const agencyId = settingsAgency.workspace_id || settingsAgency.id;
    
    try {
      await api.put(`/api/admin/agencies/${agencyId}/settings`, {
        name: settingsForm.name,
        plan: settingsForm.isLifetime ? 'lifetime' : settingsForm.plan,
        base_plan: settingsForm.plan,
        new_password: settingsForm.newPassword || undefined,
        unlimited_credits: settingsForm.unlimitedCredits,
        rate_tier: settingsForm.rateTier
      });
      toast.success('Settings saved');
      setShowSettingsModal(false);
      setSettingsAgency(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleResendInvite = async () => {
    if (!settingsAgency) return;
    const agencyId = settingsAgency.workspace_id || settingsAgency.id;
    try {
      await api.post(`/api/admin/agencies/${agencyId}/resend-invite`);
      toast.success('Invite sent');
    } catch (error) {
      toast.error('Failed to send invite');
    }
  };

  const handleChangeAgencyEmail = async () => {
    if (!settingsAgency || !changeEmailValue.trim()) return;
    const agencyId = settingsAgency.workspace_id || settingsAgency.id;
    setChangingEmail(true);
    setChangeEmailResult(null);
    try {
      const { data } = await api.post(`/api/admin/agencies/${agencyId}/change-email`, {
        new_email: changeEmailValue.trim(),
      });
      setChangeEmailResult(data);
      setChangeEmailValue('');
      // Reflect the new email in the open modal header + agency list.
      setSettingsAgency((prev) => prev ? { ...prev, email: data.new_email } : prev);
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change email');
    } finally {
      setChangingEmail(false);
    }
  };

  // Plan Config Management
  const handleSavePlanConfig = async () => {
    try {
      await api.put('/api/admin/plan-config', { 
        ...planConfig, 
        yearlyConfig 
      });
      toast.success('Plan configuration saved! Landing page updated.');
      fetchData();
    } catch (error) {
      toast.error('Failed to save plan config');
    }
  };

  const handleMovePlan = (index, direction) => {
    if (!planConfig?.plans) return;
    const newPlans = [...planConfig.plans];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newPlans.length) return;
    [newPlans[index], newPlans[newIndex]] = [newPlans[newIndex], newPlans[index]];
    newPlans.forEach((p, i) => p.order = i);
    setPlanConfig({ ...planConfig, plans: newPlans });
  };

  const handleAddPlan = () => {
    if (!newPlanForm.name) return toast.error('Plan name required');
    const newPlan = {
      id: `plan_${Date.now()}`,
      name: newPlanForm.name,
      monthly: parseInt(newPlanForm.monthly) || 0,
      voiceRate: newPlanForm.voiceRate,
      minutes: parseInt(newPlanForm.minutes) || 0,
      subAccounts: parseInt(newPlanForm.subAccounts) || 1,
      color: newPlanForm.color,
      order: planConfig?.plans?.length || 0,
      features: []
    };
    setPlanConfig(prev => ({ ...prev, plans: [...(prev?.plans || []), newPlan] }));
    setNewPlanForm({ name: '', monthly: 0, voiceRate: '0.21', minutes: 0, subAccounts: 1, color: '#14b8a6' });
  };

  const handleDeletePlan = (planId) => {
    if (!window.confirm('Delete this plan?')) return;
    setPlanConfig(prev => ({
      ...prev,
      plans: prev.plans.filter(p => p.id !== planId).map((p, i) => ({ ...p, order: i }))
    }));
  };

  const handleUpdatePlan = (planId, field, value) => {
    setPlanConfig(prev => ({
      ...prev,
      plans: prev.plans.map(p => p.id === planId ? { ...p, [field]: value } : p)
    }));
  };

  const filteredAgencies = agencies.filter(agency => {
    const matchesSearch = (agency.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (agency.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (agency.workspace_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || agency.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => ({
    active: 'text-emerald-400 bg-emerald-400/10',
    suspended: 'text-red-400 bg-red-400/10',
    paused: 'text-amber-400 bg-amber-400/10',
    grace: 'text-amber-400 bg-amber-400/10',
    failed: 'text-red-400 bg-red-400/10',
    free_month: 'text-violet-400 bg-violet-400/10',
    lifetime: 'text-amber-400 bg-amber-400/10',
    archived: 'text-gray-400 bg-gray-400/10'
  }[status] || 'text-gray-400 bg-gray-400/10');

  // Normalize display status — lifetime should not occupy the STATUS column;
  // it's already represented by the plan badge. Fall back to operational state.
  const displayStatus = (agency) => {
    const s = agency?.status;
    if (s === 'lifetime') return 'active';
    return s || 'active';
  };

  // Single source of truth for lifetime detection (handles legacy data).
  const isLifetimeAgency = (agency) => {
    if (!agency) return false;
    return agency.is_lifetime === true || agency.plan === 'lifetime' || agency.status === 'lifetime';
  };

  // Normalize plan for display — lifetime agencies always show their real plan (Pro).
  const displayPlan = (agency) => {
    if (!agency) return 'launchpad';
    if (isLifetimeAgency(agency)) {
      return agency.lifetime_base_plan || (agency.plan !== 'lifetime' ? agency.plan : 'pro') || 'pro';
    }
    return agency.plan || 'launchpad';
  };

  const getPlanColor = (plan) => ({
    free: 'text-gray-400 bg-gray-400/10',
    starter: 'text-teal-400 bg-teal-400/10',
    pro: 'text-violet-400 bg-violet-400/10',
    agency: 'text-blue-400 bg-blue-400/10',
    lifetime: 'text-amber-400 bg-amber-400/10'
  }[plan] || 'text-gray-400 bg-gray-400/10');

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Loading...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const cardClass = isDarkMode 
    ? 'bg-white/[0.02] border border-white/[0.05] backdrop-blur-sm' 
    : 'bg-white border border-gray-200 shadow-sm';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const inputClass = isDarkMode 
    ? 'bg-white/[0.03] border-white/[0.08] text-white placeholder-gray-500' 
    : 'bg-gray-50 border-gray-200 text-gray-900';

  return (
    <DashboardLayout>
    <div className="p-6 lg:p-8" data-testid="admin-dashboard">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-semibold ${textPrimary}`}>Admin Panel</h1>
          <p className={`text-sm ${textSecondary}`}>Manage platform</p>
        </div>
        <button onClick={fetchData} className={`p-2 rounded-lg ${textSecondary} hover:bg-white/5`} title="Refresh data">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Quick Stats moved to the Dashboard tab */}

        {/* Sidebar + Content Layout */}
        <div className="flex flex-col md:flex-row gap-2 md:gap-6">
          {/* Left Sidebar */}
          <div className="w-48 shrink-0 hidden md:block">
            <nav className="space-y-1 sticky top-20">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
                { id: 'agencies', label: 'Agencies', icon: Building2 },
                { id: 'create', label: 'Create', icon: Plus },
                { id: 'pricing', label: 'Plans', icon: Tag },
                { id: 'billing', label: 'Billing Rates', icon: CreditCard },
                { id: 'team', label: 'Team', icon: Users },
                { id: 'users', label: 'Users', icon: Users },
                { id: 'feedback', label: 'Feedback', icon: MessageCircle, badge: feedbackUnread },
                { id: 'settings', label: 'Settings', icon: Settings },
                { id: 'archived', label: 'Archived', icon: Archive },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? `${isDarkMode ? 'bg-white/10' : 'bg-gray-100'} ${textPrimary}`
                      : `${textSecondary} hover:${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon size={16} className={activeTab === tab.id ? 'text-teal-400' : ''} />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Mobile Tabs (horizontal, shown on small screens) */}
          <div className="flex gap-1 mb-4 overflow-x-auto pb-2 md:hidden w-full">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
              { id: 'agencies', label: 'Agencies', icon: Building2 },
              { id: 'create', label: 'Create', icon: Plus },
              { id: 'pricing', label: 'Plans', icon: Tag },
              { id: 'billing', label: 'Billing', icon: CreditCard },
              { id: 'team', label: 'Team', icon: Users },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'feedback', label: 'Feedback', icon: MessageCircle, badge: feedbackUnread },
              { id: 'settings', label: 'Settings', icon: Settings },
              { id: 'archived', label: 'Archived', icon: Archive },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                    : `${textSecondary} hover:bg-white/5`
                }`}
                data-testid={`tab-mobile-${tab.id}`}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Right Content */}
          <div className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="admin-dashboard-tab">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="admin-kpi-grid">
                {[
                  { label: 'MRR', value: formatCurrency(stats?.total_mrr || 0), icon: DollarSign, color: 'text-emerald-400', testid: 'kpi-mrr' },
                  { label: 'Agencies', value: stats?.total_accounts || 0, icon: Building2, color: 'text-teal-400', testid: 'kpi-agencies' },
                  {
                    label: 'Active in last 7d',
                    value: `${agencyActivity.active_last_7d ?? 0} / ${agencyActivity.total_agencies ?? (stats?.total_accounts || 0)}`,
                    icon: Activity,
                    color: 'text-violet-400',
                    testid: 'kpi-active-7d',
                  },
                ].map((stat, i) => (
                  <div key={i} className={`${cardClass} rounded-xl p-4`} data-testid={stat.testid}>
                    <div className="flex items-center justify-between mb-2">
                      <stat.icon className={stat.color} size={18} />
                    </div>
                    <p className={`text-xl font-semibold ${textPrimary}`}>{stat.value}</p>
                    <p className={`text-xs ${textSecondary}`}>{stat.label}</p>
                  </div>
                ))}
              </div>
              <CapacityPanel />
              <EnterpriseLeadsPanel />
              <PaygTransitionPanel />
            </motion.div>
          )}
          {/* AGENCIES TAB */}
          {activeTab === 'agencies' && (
            <motion.div key="agencies" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className={`${cardClass} rounded-xl overflow-hidden`}>
                <div className="p-4 border-b border-white/[0.05]">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                      <input
                        type="text"
                        placeholder="Search agencies..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full pl-9 pr-4 py-2 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50 text-sm`}
                      />
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className={`px-3 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="lifetime">Lifetime</option>
                      <option value="paused">Paused</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Agency</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase hidden sm:table-cell`}>ID</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Accounts</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase hidden md:table-cell`}>Balance</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Plan</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase hidden md:table-cell`}>MRR</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase hidden lg:table-cell`}>Promo</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase hidden md:table-cell`}>Last Activity</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Status</th>
                        <th className={`px-4 py-3 text-right text-xs font-medium ${textSecondary} uppercase`}></th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-white/[0.05]' : 'divide-gray-100'}`}>
                      {filteredAgencies.map((agency) => (
                        <tr key={agency.id || agency.email} className={`${isDarkMode ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                                style={{ backgroundColor: agency.color || '#14b8a6' }}
                              >
                                {agency.name?.charAt(0) || '?'}
                              </div>
                              <div className="min-w-0">
                                <p className={`font-medium ${textPrimary} truncate text-sm`}>{agency.name}</p>
                                <p className={`text-xs ${textSecondary} truncate`}>{agency.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <div className="flex items-center gap-1">
                              <code className={`text-xs ${textSecondary} font-mono`}>{agency.workspace_id?.substring(0, 8)}...</code>
                              <button onClick={() => copyToClipboard(agency.workspace_id)} className="p-1 hover:bg-white/10 rounded">
                                <Copy size={10} className="text-gray-500" />
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${textPrimary}`}>
                              {agency.sub_accounts || 0}
                              <span className={textSecondary}>/{agency.sub_account_limit === 999 ? '∞' : agency.sub_account_limit || 1}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {agency.unlimited_credits ? (
                                <UnlimitedCostPill api={api} workspaceId={agency.workspace_id} />
                              ) : (
                                <span className={`text-sm ${(agency.wallet_balance || 0) < 0 ? 'text-red-400' : textPrimary}`}>
                                  {formatCurrency(agency.wallet_balance || 0)}
                                </span>
                              )}
                              {agency.rate_tier === 'cost' && (
                                <span
                                  className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400/15 text-amber-300 border border-amber-400/30"
                                  data-testid={`cost-tier-badge-${agency.workspace_id}`}
                                  title="At-cost rate tier"
                                >
                                  COST
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPlanColor(displayPlan(agency))} inline-flex items-center gap-1`} data-testid={`plan-chip-${agency.workspace_id}`}>
                              {isLifetimeAgency(agency) && <Infinity size={10} className="text-amber-400" />}
                              {displayPlan(agency)}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            {isLifetimeAgency(agency) ? (
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-amber-500/20 to-yellow-400/20 text-amber-300 border border-amber-400/30" data-testid={`mrr-lifetime-${agency.workspace_id}`}>
                                LIFETIME
                              </span>
                            ) : agency.is_beta ? (
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold border"
                                    style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f', borderColor: 'rgba(148,163,184,0.5)' }}
                                    data-testid={`mrr-beta-${agency.workspace_id}`}>
                                BETA
                              </span>
                            ) : (
                              <span className={`text-sm ${textPrimary}`}>{formatCurrency(agency.mrr)}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <AgencyPromoCell agency={agency} api={api} activePromos={activePromos} onChanged={fetchData} />
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell" data-testid={`last-activity-${agency.workspace_id}`}>
                            <LastActivityCell
                              activity={agencyActivity.workspaces?.[agency.workspace_id]}
                              textSecondary={textSecondary}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(displayStatus(agency))}`} data-testid={`status-chip-${agency.workspace_id}`}>
                              {displayStatus(agency)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                ref={el => menuAnchorRefs.current[agency.email] = el}
                                onClick={() => setShowAgencyMenu(showAgencyMenu === agency.email ? null : agency.email)}
                                className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'} ${textSecondary}`}
                                data-testid={`agency-menu-${agency.workspace_id}`}
                              >
                                <MoreVertical size={16} />
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteConfirmAgency({ id: agency.workspace_id || agency.id, name: agency.name });
                                }}
                                className="p-1.5 rounded-lg hover:bg-red-400/15 text-gray-500 hover:text-red-400 transition-colors"
                                title="Delete agency"
                                data-testid={`agency-delete-${agency.workspace_id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            
                            {showAgencyMenu === agency.email && ReactDOM.createPortal(
                              <div
                                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                                onClick={(e) => { if (e.target === e.currentTarget) setShowAgencyMenu(null); }}
                                data-testid="agency-actions-overlay"
                              >
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  className={`w-full max-w-md ${isDarkMode ? 'bg-[#0c0d12] border-white/[0.08]' : 'bg-white border-gray-200'} border rounded-2xl shadow-2xl`}
                                  onClick={e => e.stopPropagation()}
                                >
                                  {/* Header */}
                                  <div className={`px-6 py-5 border-b ${isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'}`}>
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 rounded-xl flex items-center justify-center text-base font-bold text-white shadow-lg" style={{ background: agency.color || '#14b8a6' }}>
                                          {(agency.name || '?')[0]}
                                        </div>
                                        <div>
                                          <p className={`text-base font-semibold ${textPrimary}`}>{agency.name}</p>
                                          <p className={`text-xs ${textSecondary}`}>{agency.email}</p>
                                        </div>
                                      </div>
                                      <button onClick={() => setShowAgencyMenu(null)} className={`p-2 rounded-xl ${isDarkMode ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-100'} transition-colors`}>
                                        <X size={18} className={textSecondary} />
                                      </button>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold ${getStatusColor(displayStatus(agency))}`}>{displayStatus(agency).toUpperCase()}</span>
                                      <span className={`px-2.5 py-1 rounded-lg text-[11px] font-medium inline-flex items-center gap-1 ${getPlanColor(displayPlan(agency))}`}>
                                        {isLifetimeAgency(agency) && <Infinity size={10} className="text-amber-400" />}
                                        {displayPlan(agency).charAt(0).toUpperCase() + displayPlan(agency).slice(1)} Plan
                                      </span>
                                      {isLifetimeAgency(agency) && (
                                        <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-gradient-to-r from-amber-500/20 to-yellow-400/20 text-amber-300 border border-amber-400/20" data-testid="header-lifetime-badge">LIFETIME</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Status Control */}
                                  <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'}`}>
                                    <p className={`text-[10px] uppercase tracking-widest font-semibold mb-3 ${textSecondary}`}>Status</p>
                                    <div className="flex gap-2">
                                      {['active', 'paused', 'suspended'].map(s => {
                                        const isCurrent = agency.status === s;
                                        const glow = isCurrent ? (
                                          s === 'active' ? 'shadow-[0_0_24px_rgba(16,185,129,0.45)] ring-2 ring-emerald-400/40' :
                                          s === 'paused' ? 'shadow-[0_0_24px_rgba(251,191,36,0.45)] ring-2 ring-amber-400/40' :
                                          'shadow-[0_0_24px_rgba(248,113,113,0.45)] ring-2 ring-red-400/40'
                                        ) : '';
                                        return (
                                        <button
                                          key={s}
                                          onClick={() => { handleAgencyAction(agency.id, s === 'active' ? 'activate' : s === 'paused' ? 'pause' : 'suspend'); }}
                                          disabled={isCurrent}
                                          className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                                            isCurrent
                                              ? s === 'active' ? `bg-emerald-400/20 text-emerald-300 border border-emerald-400/40 ${glow}` : s === 'paused' ? `bg-amber-400/20 text-amber-300 border border-amber-400/40 ${glow}` : `bg-red-400/20 text-red-300 border border-red-400/40 ${glow}`
                                              : isDarkMode ? 'bg-white/[0.03] text-gray-500 border border-white/[0.06] hover:bg-white/[0.06]' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                                          }`}
                                          data-testid={`agency-status-btn-${s}`}
                                        >
                                          <span className="inline-flex items-center gap-1.5">
                                            {isCurrent && <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${s === 'active' ? 'bg-emerald-400' : s === 'paused' ? 'bg-amber-400' : 'bg-red-400'}`} />}
                                            {s.charAt(0).toUpperCase() + s.slice(1)}
                                          </span>
                                        </button>
                                      );})}
                                    </div>
                                  </div>

                                  {/* Quick Actions */}
                                  <div className="px-6 py-4">
                                    <p className={`text-[10px] uppercase tracking-widest font-semibold mb-3 ${textSecondary}`}>Actions</p>
                                    <div className="grid grid-cols-2 gap-2">
                                      <button onClick={() => { setShowAgencyMenu(null); handleOpenSettings(agency); }} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm ${isDarkMode ? 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'} transition-colors`}>
                                        <Settings size={16} className="text-teal-400" />
                                        <span className={textPrimary}>Settings</span>
                                      </button>
                                      <button onClick={() => { setShowAgencyMenu(null); handleOpenGrantAccess(agency); }} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm ${isDarkMode ? 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'} transition-colors`}>
                                        <UserPlus size={16} className="text-cyan-400" />
                                        <span className={textPrimary}>Grant Access</span>
                                      </button>
                                      <button onClick={() => { setShowAgencyMenu(null); handleOpenFreeMonthsModal(agency); }} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm ${isDarkMode ? 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'} transition-colors`}>
                                        <Gift size={16} className="text-violet-400" />
                                        <span className={textPrimary}>Free Months</span>
                                      </button>
                                      <button onClick={() => { setShowAgencyMenu(null); handleOpenAgencyPricing(agency); }} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm ${isDarkMode ? 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'} transition-colors`}>
                                        <Wallet size={16} className="text-emerald-400" />
                                        <span className={textPrimary}>Pricing</span>
                                      </button>
                                      {(() => {
                                        const isLife = isLifetimeAgency(agency);
                                        return (
                                          <button
                                            onClick={() => {
                                              setShowAgencyMenu(null);
                                              const verb = isLife ? 'revoke' : 'grant';
                                              const name = agency.name || 'this workspace';
                                              if (!window.confirm(`${verb === 'grant' ? 'Grant' : 'Revoke'} lifetime access ${verb === 'grant' ? 'to' : 'from'} ${name}?${verb === 'revoke' ? ' They will be restored to a paid plan.' : ''}`)) return;
                                              handleAgencyAction(agency.workspace_id, verb === 'grant' ? 'grant_lifetime' : 'revoke_lifetime');
                                            }}
                                            className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm transition-all ${
                                              isLife
                                                ? 'bg-amber-400/15 border border-amber-400/40 text-amber-200 shadow-[0_0_20px_rgba(251,191,36,0.35)]'
                                                : `${isDarkMode ? 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`
                                            }`}
                                            data-testid={`agency-lifetime-toggle-${agency.workspace_id}`}
                                          >
                                            <Infinity size={16} className={isLife ? 'text-amber-300' : 'text-amber-400'} />
                                            <span className={isLife ? 'text-amber-200 font-semibold' : textPrimary}>
                                              {isLife ? 'Lifetime ON' : 'Lifetime'}
                                            </span>
                                            {isLife && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
                                          </button>
                                        );
                                      })()}
                                      <button onClick={() => { setShowAgencyMenu(null); handleAgencyAction(agency.workspace_id, 'remind'); }} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm ${isDarkMode ? 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'} transition-colors`}>
                                        <Mail size={16} className="text-blue-400" />
                                        <span className={textPrimary}>Reminder</span>
                                      </button>
                                    </div>
                                  </div>

                                  {/* Danger Zone */}
                                  <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'}`}>
                                    <button onClick={() => { setShowAgencyMenu(null); setDeleteConfirmAgency({ id: agency.workspace_id || agency.id, name: agency.name }); }} className="w-full py-2.5 rounded-xl text-sm text-red-400 hover:bg-red-400/10 transition-colors">
                                      Delete Agency
                                    </button>
                                  </div>
                                </motion.div>
                              </div>,
                              document.body
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* CREATE WORKSPACE TAB */}
          {activeTab === 'create' && (
            <motion.div key="create" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl items-start">
              {/* ─── LEFT COLUMN — Invite flows ─── */}
              <div className="space-y-6">
              <div className={`${cardClass} rounded-xl p-6`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-teal-400/10 flex items-center justify-center">
                    <Plus className="text-teal-400" size={20} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${textPrimary}`}>Create Workspace</h3>
                    <p className={`text-sm ${textSecondary}`}>Add a new agency</p>
                  </div>
                </div>

                <form onSubmit={handleCreateWorkspace} className="space-y-4" data-testid="create-workspace-form">
                  <div>
                    <label className={`block text-sm ${textSecondary} mb-1.5`}>Business Name</label>
                    <input
                      type="text"
                      value={newWorkspace.name}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                      required
                      data-testid="create-workspace-name-input"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm ${textSecondary} mb-1.5`}>Owner Email</label>
                    <input
                      type="email"
                      value={newWorkspace.email}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, email: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                      required
                      data-testid="create-workspace-email-input"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm ${textSecondary} mb-1.5`}>Plan</label>
                    <select
                      value={newWorkspace.isLifetime ? 'pro' : newWorkspace.plan}
                      disabled={newWorkspace.isLifetime}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, plan: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg ${inputClass} border focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed`}
                      data-testid="create-workspace-plan-select"
                    >
                      {planConfig?.plans?.map(p => (
                        <option key={p.id} value={p.id}>{p.name} - ${p.monthly}/mo</option>
                      )) || (
                        <>
                          <option value="launchpad">Launchpad</option>
                          <option value="starter">Starter</option>
                          <option value="agency">Agency</option>
                          <option value="pro">Pro</option>
                        </>
                      )}
                    </select>
                    {newWorkspace.isLifetime && (
                      <p className="text-xs text-amber-400 mt-1">Lifetime = Pro plan (locked)</p>
                    )}
                  </div>

                  <div className="rounded-lg border border-amber-400/20 bg-amber-400/5 p-3 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newWorkspace.isLifetime}
                        onChange={(e) => setNewWorkspace({
                          ...newWorkspace,
                          isLifetime: e.target.checked,
                          // auto-select Pro when enabling lifetime
                          plan: e.target.checked ? 'pro' : newWorkspace.plan,
                          free_months: 0,
                        })}
                        className="w-4 h-4 rounded"
                        data-testid="create-workspace-lifetime-toggle"
                      />
                      <span className={`text-sm font-medium ${textPrimary}`}>Grant Lifetime Access</span>
                      <span className="text-xs text-amber-400 ml-auto">$0/mo forever</span>
                    </label>
                    <p className={`text-xs ${textSecondary}`}>Lifetime users are Pro plan with unlimited sub-accounts. They still add CC on file for usage top-ups.</p>
                  </div>

                  {!newWorkspace.isLifetime && (
                    <div>
                      <label className={`block text-sm ${textSecondary} mb-1.5`}>Free Trial Months</label>
                      <input
                        type="number"
                        min="0"
                        max="12"
                        value={newWorkspace.free_months}
                        onChange={(e) => setNewWorkspace({ ...newWorkspace, free_months: e.target.value })}
                        className={`w-full px-3 py-2.5 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                        placeholder="0"
                        data-testid="create-workspace-free-months"
                      />
                      <p className={`text-xs ${textSecondary} mt-1`}>
                        CC is captured on signup; auto-charges begin after {newWorkspace.free_months || 0} month(s).
                      </p>
                    </div>
                  )}

                  <div>
                    <label className={`block text-sm ${textSecondary} mb-1.5`}>Promotion Code (optional)</label>
                    <select
                      value={newWorkspace.promo_id || ''}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, promo_id: e.target.value })}
                      className={`w-full px-3 py-2.5 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                      data-testid="create-workspace-promo-select"
                    >
                      <option value="">— None —</option>
                      {activePromos
                        .filter(p => p.applies_to?.includes(newWorkspace.isLifetime ? 'lifetime' : 'monthly'))
                        .map(p => (
                          <option key={p.promotion_id} value={p.promotion_id}>
                            {p.code} — {p.discount_type === 'percent' ? `${p.discount_value}% off` : `$${p.discount_value} off`}
                            {p.duration === 'repeating' ? ` · ${p.duration_in_months}mo` : p.duration === 'forever' ? ' · forever' : ' · once'}
                            {p.trial_days > 0 ? ` · +${p.trial_days}d trial` : ''}
                          </option>
                        ))}
                    </select>
                    <p className={`text-xs ${textSecondary} mt-1`}>
                      Auto-applied at Stripe Checkout when the user pays. Manage codes in the Promotions panel →
                    </p>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newWorkspace.sendInvite}
                      onChange={(e) => setNewWorkspace({ ...newWorkspace, sendInvite: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <span className={`text-sm ${textSecondary}`}>Send invite email</span>
                  </label>

                  <button
                    type="submit"
                    disabled={creatingWorkspace}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
                    data-testid="create-workspace-submit-btn"
                  >
                    {creatingWorkspace ? <div className="w-5 h-5 border-2 border-[#0a0b0f]/30 border-t-[#0a0b0f] rounded-full animate-spin" /> : <Plus size={18} />}
                    Create Workspace
                  </button>
                </form>
              </div>

              {/* Beta Program Access — inside left column */}
              <div className={`${cardClass} rounded-xl p-6 hidden`} data-testid="beta-program-access-card">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                         style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)' }}>
                      <Sparkles className="text-[#0a0b0f]" size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${textPrimary}`}>Beta Program Access</h3>
                        <span className="px-1.5 rounded text-[9px] font-extrabold tracking-[0.2em] border"
                              style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f', borderColor: 'rgba(148,163,184,0.5)' }}>
                          BETA
                        </span>
                      </div>
                      <p className={`text-sm ${textSecondary}`}>
                        Invite VIP beta users. Agency-tier access, $0 MRR, usage-based billing.
                        {betaProgramStatus?.ended && <span className="ml-2 text-amber-400 font-medium">(Program ended)</span>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs ${textSecondary} uppercase tracking-wider`}>Active beta users</div>
                    <div className={`text-2xl font-bold ${textPrimary}`} data-testid="beta-total-count">{betaProgramStatus?.total_beta_workspaces || 0}</div>
                  </div>
                </div>

                {!betaInvitePreview ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      value={newBetaUser.name}
                      onChange={(e) => setNewBetaUser({ ...newBetaUser, name: e.target.value })}
                      placeholder="Business name"
                      className={`px-3 py-2.5 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                      data-testid="beta-invite-business-input"
                    />
                    <input
                      type="text"
                      value={newBetaUser.person_name}
                      onChange={(e) => setNewBetaUser({ ...newBetaUser, person_name: e.target.value })}
                      placeholder="Person's name"
                      className={`px-3 py-2.5 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                      data-testid="beta-invite-person-input"
                    />
                    <input
                      type="email"
                      value={newBetaUser.email}
                      onChange={(e) => setNewBetaUser({ ...newBetaUser, email: e.target.value })}
                      placeholder="Owner email"
                      className={`px-3 py-2.5 rounded-lg ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
                      data-testid="beta-invite-email-input"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!newBetaUser.email || !newBetaUser.name || !newBetaUser.person_name) {
                          toast.error('Enter business name, person\u2019s name and email first');
                          return;
                        }
                        setBetaInvitePreview(true);
                      }}
                      className="md:col-span-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-colors"
                      style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f' }}
                      data-testid="beta-invite-preview-btn"
                    >
                      <Sparkles size={16} /> Preview Beta Invite
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="beta-invite-preview-pane">
                    <div className={`rounded-xl border ${isDarkMode ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'} p-5`}>
                      <div className="text-xs uppercase tracking-wider text-teal-400 mb-2">Invite Preview</div>
                      <div className="space-y-1.5 text-sm">
                        <div><span className={textSecondary}>Business:</span> <span className={`${textPrimary} font-medium`}>{newBetaUser.name}</span></div>
                        <div><span className={textSecondary}>Contact:</span> <span className={`${textPrimary} font-medium`}>{newBetaUser.person_name} &lt;{newBetaUser.email}&gt;</span></div>
                        <div><span className={textSecondary}>Subject:</span> <span className={textPrimary}>You're in — Welcome to the Voxinity AI Beta Program</span></div>
                        <div><span className={textSecondary}>Plan:</span> <span className={textPrimary}>Agency tier · $0 MRR · Pay-as-you-go balance</span></div>
                        <div><span className={textSecondary}>Lifetime offer:</span> <span className="text-amber-300 font-semibold">$3,000 flat (while beta active)</span></div>
                        <div><span className={textSecondary}>Webhook:</span> <span className={textPrimary}>Fires <code className="text-teal-300">beta_invited</code> event to your configured URL</span></div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setBetaInvitePreview(false)}
                        className={`px-4 py-2.5 rounded-xl text-sm font-medium ${isDarkMode ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        data-testid="beta-invite-edit-btn"
                      >
                        ← Edit
                      </button>
                      <button
                        type="button"
                        onClick={handleBetaInviteSubmit}
                        disabled={sendingBetaInvite}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f' }}
                        data-testid="beta-invite-confirm-btn"
                      >
                        {sendingBetaInvite ? <div className="w-4 h-4 border-2 border-[#0a0b0f]/30 border-t-[#0a0b0f] rounded-full animate-spin" /> : <Sparkles size={16} />}
                        Confirm &amp; Send Beta Invite
                      </button>
                    </div>
                  </div>
                )}
              </div>
              </div>

              {/* ─── RIGHT COLUMN — Discount & trial codes ─── */}
              <div className="space-y-6">
                <TrialCodesPanel isDarkMode={isDarkMode} />
                <PromotionsPanel isDarkMode={isDarkMode} />
              </div>
            </motion.div>
          )}

          {/* PRICING TAB */}
          {activeTab === 'pricing' && (
            <motion.div key="pricing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className={`${cardClass} rounded-xl p-6`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-400/10 flex items-center justify-center">
                      <Tag className="text-violet-400" size={20} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>Plan Configuration</h3>
                      <p className={`text-sm ${textSecondary}`}>Manage plans & pricing</p>
                    </div>
                  </div>
                  <button
                    onClick={handleSavePlanConfig}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-600 transition-colors"
                  >
                    <Check size={16} /> Save & Update Landing
                  </button>
                </div>

                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-amber-400/5 border border-amber-400/20' : 'bg-amber-50 border border-amber-200'} mb-6`}>
                  <div className="flex items-start gap-2">
                    <Crown className="text-amber-400 flex-shrink-0 mt-0.5" size={14} />
                    <p className={`text-xs ${isDarkMode ? 'text-amber-200' : 'text-amber-700'}`}>
                      <strong>Grandfathering:</strong> Existing agencies keep their original signup price. New prices only apply to new signups.
                    </p>
                  </div>
                </div>

                {/* Plan List */}
                <div className="space-y-3 mb-6">
                  {(planConfig?.plans || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((plan, index) => (
                    <div key={plan.id} className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/[0.02] border border-white/[0.05]' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => handleMovePlan(index, -1)}
                            disabled={index === 0}
                            className={`p-0.5 rounded ${textSecondary} hover:bg-white/10 disabled:opacity-30`}
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            onClick={() => handleMovePlan(index, 1)}
                            disabled={index === (planConfig?.plans?.length || 0) - 1}
                            className={`p-0.5 rounded ${textSecondary} hover:bg-white/10 disabled:opacity-30`}
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: plan.color || '#14b8a6' }}
                        />
                        <input
                          type="text"
                          value={plan.name}
                          onChange={(e) => handleUpdatePlan(plan.id, 'name', e.target.value)}
                          className={`flex-1 px-2 py-1 rounded ${inputClass} border focus:outline-none font-medium`}
                        />
                        <input
                          type="color"
                          value={plan.color || '#14b8a6'}
                          onChange={(e) => handleUpdatePlan(plan.id, 'color', e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border-0"
                        />
                        <button
                          onClick={() => handleDeletePlan(plan.id)}
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-400/10"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className={`block text-xs ${textSecondary} mb-1`}>Monthly ($)</label>
                          <div className="relative">
                            <DollarSign size={12} className={`absolute left-2 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                            <input
                              type="number"
                              value={plan.monthly}
                              onChange={(e) => handleUpdatePlan(plan.id, 'monthly', parseInt(e.target.value) || 0)}
                              className={`w-full pl-6 pr-2 py-1.5 rounded ${inputClass} border focus:outline-none text-sm`}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={`block text-xs ${textSecondary} mb-1`}>Voice Rate</label>
                          <div className="relative">
                            <Mic size={12} className={`absolute left-2 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                            <input
                              type="text"
                              value={plan.voiceRate}
                              onChange={(e) => handleUpdatePlan(plan.id, 'voiceRate', e.target.value)}
                              className={`w-full pl-6 pr-2 py-1.5 rounded ${inputClass} border focus:outline-none text-sm`}
                              placeholder="$0.21/min"
                            />
                          </div>
                        </div>
                        <div>
                          <label className={`block text-xs ${textSecondary} mb-1`}>Minutes</label>
                          <input
                            type="number"
                            value={plan.minutes}
                            onChange={(e) => handleUpdatePlan(plan.id, 'minutes', parseInt(e.target.value) || 0)}
                            className={`w-full px-2 py-1.5 rounded ${inputClass} border focus:outline-none text-sm`}
                          />
                        </div>
                        <div>
                          <label className={`block text-xs ${textSecondary} mb-1`}>Sub-Accounts</label>
                          <input
                            type="number"
                            value={plan.subAccounts}
                            onChange={(e) => handleUpdatePlan(plan.id, 'subAccounts', parseInt(e.target.value) || 1)}
                            className={`w-full px-2 py-1.5 rounded ${inputClass} border focus:outline-none text-sm`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add New Plan */}
                <div className={`p-4 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-white/10' : 'border-gray-300'} mb-6`}>
                  <h4 className={`text-sm font-medium ${textPrimary} mb-3`}>Add New Plan</h4>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <input
                      type="text"
                      value={newPlanForm.name}
                      onChange={(e) => setNewPlanForm({ ...newPlanForm, name: e.target.value })}
                      placeholder="Plan name"
                      className={`col-span-2 px-3 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                    />
                    <input
                      type="number"
                      value={newPlanForm.monthly}
                      onChange={(e) => setNewPlanForm({ ...newPlanForm, monthly: e.target.value })}
                      placeholder="$/mo"
                      className={`px-3 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                    />
                    <input
                      type="text"
                      value={newPlanForm.voiceRate}
                      onChange={(e) => setNewPlanForm({ ...newPlanForm, voiceRate: e.target.value })}
                      placeholder="Voice rate"
                      className={`px-3 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                    />
                    <input
                      type="number"
                      value={newPlanForm.subAccounts}
                      onChange={(e) => setNewPlanForm({ ...newPlanForm, subAccounts: e.target.value })}
                      placeholder="Accounts"
                      className={`px-3 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                    />
                    <button
                      onClick={handleAddPlan}
                      className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-teal-400/10 text-teal-400 hover:bg-teal-400/20 text-sm font-medium"
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </div>

                {/* Yearly Pricing Config */}
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/[0.02] border border-white/[0.05]' : 'bg-gray-50 border border-gray-200'}`}>
                  <h4 className={`text-sm font-medium ${textPrimary} mb-3`}>Yearly Pricing Discount</h4>
                  <div className="flex items-center gap-4">
                    <select
                      value={yearlyConfig.type}
                      onChange={(e) => setYearlyConfig({ ...yearlyConfig, type: e.target.value })}
                      className={`px-3 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                    >
                      <option value="percentage">Percentage Off</option>
                      <option value="fixed">Fixed Amount Off (per month)</option>
                    </select>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={yearlyConfig.value}
                        onChange={(e) => setYearlyConfig({ ...yearlyConfig, value: parseFloat(e.target.value) || 0 })}
                        className={`w-24 px-3 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                      />
                      <span className={`text-sm ${textSecondary}`}>
                        {yearlyConfig.type === 'percentage' ? '%' : '$/mo'}
                      </span>
                    </div>
                    <span className={`text-xs ${textSecondary}`}>
                      Example: $297/mo becomes ${yearlyConfig.type === 'percentage' 
                        ? Math.round(297 * (1 - yearlyConfig.value / 100)) 
                        : Math.max(0, 297 - yearlyConfig.value)}/mo yearly
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* TEAM TAB */}
          {activeTab === 'team' && (
            <motion.div key="team" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg">
              <div className={`${cardClass} rounded-xl p-6`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-400/10 flex items-center justify-center">
                      <Users className="text-teal-400" size={20} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>Team</h3>
                      <p className={`text-sm ${textSecondary}`}>Admin access</p>
                    </div>
                  </div>
                  {user?.role === 'super_admin' && (
                    <button
                      onClick={() => setShowAddTeamModal(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-400/10 text-teal-400 hover:bg-teal-400/20 text-sm font-medium"
                    >
                      <UserPlus size={16} /> Add
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  {teamMembers.map((member) => (
                    <div key={member.user_id || member.id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-violet-400 flex items-center justify-center text-white text-sm font-medium">
                          {member.name?.charAt(0)}
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${textPrimary}`}>{member.name}</p>
                          <p className={`text-xs ${textSecondary}`}>{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          member.role === 'super_admin' ? 'bg-violet-400/10 text-violet-400' : 'bg-teal-400/10 text-teal-400'
                        }`}>
                          {member.role === 'super_admin' ? 'Super' : 'Admin'}
                        </span>
                        {user?.role === 'super_admin' && member.user_id !== user.user_id && member.id !== user.id && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveTeamMember(member.user_id || member.id); }}
                            className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                              confirmDeleteId === (member.user_id || member.id)
                                ? 'bg-red-500 text-white hover:bg-red-600'
                                : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            data-testid={`delete-team-member-${member.user_id || member.id}`}
                          >
                            {confirmDeleteId === (member.user_id || member.id) ? 'Confirm?' : <Trash2 size={14} />}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* USERS TAB */}
          {activeTab === 'users' && (
            <motion.div key="users" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="users-panel">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className={`font-semibold ${textPrimary}`}>All Users</h3>
                  <p className={`text-sm ${textSecondary}`}>Owners, access-only members and orphaned accounts — review and remove stray records.</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                    <input
                      value={usersSearch}
                      onChange={(e) => setUsersSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                      placeholder="Search email or name…"
                      className={`pl-9 pr-3 py-2 rounded-xl ${inputClass} border text-sm w-56`}
                      data-testid="users-search-input"
                    />
                  </div>
                  <button onClick={fetchUsers} className={`p-2 rounded-xl ${isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} ${textSecondary}`} data-testid="users-refresh-btn">
                    <RefreshCw size={16} className={loadingUsers ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Filter pills */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'owner', label: 'Owners' },
                  { id: 'access_only', label: 'Access only' },
                  { id: 'orphan', label: 'Orphans' },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setUsersFilter(f.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      usersFilter === f.id
                        ? 'bg-teal-400/15 text-teal-400 border border-teal-400/30'
                        : `${textSecondary} border ${isDarkMode ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`
                    }`}
                    data-testid={`users-filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {loadingUsers ? (
                <p className={`text-sm ${textSecondary} py-8 text-center`}>Loading users…</p>
              ) : usersList.length === 0 ? (
                <p className={`text-sm ${textSecondary} py-8 text-center`}>No users found.</p>
              ) : (
                <div className={`rounded-xl border ${isDarkMode ? 'border-white/10' : 'border-gray-200'} overflow-hidden`}>
                  {usersList.map((u, idx) => (
                    <div
                      key={u.user_id}
                      className={`flex items-center gap-3 px-4 py-3 ${idx !== usersList.length - 1 ? `border-b ${isDarkMode ? 'border-white/5' : 'border-gray-100'}` : ''} ${isDarkMode ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'}`}
                      data-testid={`user-row-${u.user_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${textPrimary} truncate`}>{u.email}</span>
                          {u.category === 'owner' && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-400/15 text-amber-400 text-[10px] font-semibold"><Crown size={10} /> Owner</span>
                          )}
                          {u.category === 'access_only' && (
                            <span className="px-2 py-0.5 rounded-md bg-sky-400/15 text-sky-400 text-[10px] font-semibold">Access only</span>
                          )}
                          {u.category === 'orphan' && (
                            <span className="px-2 py-0.5 rounded-md bg-gray-400/15 text-gray-400 text-[10px] font-semibold">Orphan</span>
                          )}
                          {u.is_admin && (
                            <span className="px-2 py-0.5 rounded-md bg-purple-400/15 text-purple-400 text-[10px] font-semibold">Admin</span>
                          )}
                        </div>
                        <p className={`text-xs ${textSecondary} truncate`}>
                          {u.name || '—'}
                          {u.category === 'owner' && ` · owns ${u.owned_workspaces.map(w => w.name || w.workspace_id).join(', ')}`}
                          {u.category === 'access_only' && ` · access to ${u.access_workspace_count} workspace(s)`}
                          {u.category === 'orphan' && u.is_invited && ' · invite placeholder'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteUser(u)}
                        disabled={u.is_admin}
                        title={u.is_admin ? 'Manage admins in the Team tab' : 'Delete user'}
                        className={`p-2 rounded-lg transition-all ${u.is_admin ? 'opacity-30 cursor-not-allowed' : 'text-rose-400 hover:bg-rose-500/10'}`}
                        data-testid={`user-delete-${u.user_id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className={`text-xs ${textSecondary} mt-3`}>{usersList.length} user(s) shown</p>
            </motion.div>
          )}


          {/* BILLING RATES TAB */}
          {activeTab === 'billing' && (
            <motion.div key="billing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <BillingRatesPanel api={api} isDarkMode={isDarkMode} cardClass={cardClass} textPrimary={textPrimary} textSecondary={textSecondary} inputClass={inputClass} />
            </motion.div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <motion.div key="settings" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="space-y-6">
                {/* Beta Program Global Toggle */}
                <div className={`${cardClass} rounded-2xl overflow-hidden`} data-testid="beta-program-settings-card">
                  <div className="p-6 border-b border-white/[0.06]">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                             style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)' }}>
                          <Sparkles size={20} className="text-[#0a0b0f]" />
                        </div>
                        <div>
                          <h3 className={`text-lg font-semibold ${textPrimary}`}>Beta Launch Program</h3>
                          <p className={`text-sm ${textSecondary}`}>
                            {betaProgramStatus?.ended
                              ? 'Program is ENDED — all beta users are being forced to choose (LIFETIME price is $5,000).'
                              : `Program is ACTIVE — beta users see welcome popup + $3,000 LIFETIME urgency offer.`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs ${textSecondary}`}>{betaProgramStatus?.total_beta_workspaces || 0} active beta workspaces</span>
                        {betaProgramStatus?.ended ? (
                          <button
                            onClick={() => handleToggleBetaProgram(false)}
                            disabled={togglingBetaProgram}
                            className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider border bg-amber-500/20 text-amber-400 border-amber-500/50 disabled:opacity-50"
                            data-testid="reopen-beta-program-btn"
                          >
                            {togglingBetaProgram ? 'WORKING…' : 'RE-OPEN BETA'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleToggleBetaProgram(true)}
                            disabled={togglingBetaProgram}
                            className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider border bg-rose-500/20 text-rose-400 border-rose-500/50 disabled:opacity-50"
                            data-testid="end-beta-program-btn"
                          >
                            {togglingBetaProgram ? 'WORKING…' : 'END BETA PROGRAM'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className={`mt-4 text-xs ${textSecondary}`}>
                      <strong className={textPrimary}>What ending the beta does:</strong> (1) All beta users see a mandatory
                      modal forcing them to pick monthly plan / LIFETIME / schedule a call, (2) LIFETIME price flips from
                      $3,000 to $5,000, (3) Welcome popup is replaced by the transition modal.
                    </div>
                  </div>

                  {/* ── Countdown picker ──────────────────────────────
                      Set a future end date and the beta program will
                      auto-end the moment the countdown hits zero.
                      Beta users see a live "BETA ENDS IN …" badge in
                      their dashboard header during the countdown.
                      Operator can change, extend, or clear the
                      countdown anytime. */}
                  {!betaProgramStatus?.ended && (
                    <div className={`p-6 border-t ${isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'}`} data-testid="beta-countdown-config">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                             style={{ background: 'linear-gradient(135deg,#f43f5e,#e11d48)' }}>
                          <Clock size={16} className="text-white" />
                        </div>
                        <div>
                          <h4 className={`text-sm font-semibold ${textPrimary}`}>Beta Countdown</h4>
                          <p className={`text-xs ${textSecondary}`}>
                            Schedule the program to auto-end on a future date. Beta users see a live countdown + $3K lifetime offer popup every 48h until the timer hits zero.
                          </p>
                        </div>
                      </div>

                      {betaProgramStatus?.ends_at ? (
                        <div className="space-y-3">
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-rose-500/[0.08] border-rose-500/30' : 'bg-rose-50 border-rose-200'} border`} data-testid="beta-countdown-active-card">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div>
                                <p className={`text-[10px] uppercase tracking-wider ${textSecondary}`}>Beta ends</p>
                                <p className={`text-base font-semibold ${textPrimary}`} data-testid="beta-countdown-ends-at">
                                  {new Date(betaProgramStatus.ends_at).toLocaleString(undefined, {
                                    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                                    hour: 'numeric', minute: '2-digit',
                                  })}
                                </p>
                                <p className={`text-xs ${textSecondary} mt-1`}>
                                  ~{Math.floor((betaProgramStatus.countdown_seconds || 0) / 86400)}d{' '}
                                  {Math.floor(((betaProgramStatus.countdown_seconds || 0) % 86400) / 3600)}h from now
                                </p>
                              </div>
                              <button
                                onClick={() => handleSetBetaEndDate(null)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${isDarkMode ? 'bg-white/[0.04] border-white/[0.08] text-white/70 hover:bg-white/[0.08]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                data-testid="beta-countdown-clear-btn"
                              >
                                Clear countdown
                              </button>
                            </div>
                          </div>
                          <p className={`text-[11px] ${textSecondary}`}>
                            To change the date, set a new one below — it replaces the current countdown.
                          </p>
                        </div>
                      ) : (
                        <p className={`text-xs ${textSecondary} mb-3`}>No countdown scheduled. Pick one below to start the urgency timer.</p>
                      )}

                      <div className="mt-4">
                        <p className={`text-[10px] uppercase tracking-wider ${textSecondary} font-semibold mb-2`}>Quick presets</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {[
                            { label: '1 Week', weeks: 1 },
                            { label: '2 Weeks', weeks: 2 },
                            { label: '3 Weeks', weeks: 3 },
                            { label: '4 Weeks', weeks: 4 },
                          ].map(p => (
                            <button
                              key={p.weeks}
                              onClick={() => {
                                const d = new Date();
                                d.setDate(d.getDate() + p.weeks * 7);
                                handleSetBetaEndDate(d.toISOString());
                              }}
                              className={`px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${isDarkMode ? 'bg-white/[0.04] border-white/[0.08] text-white/80 hover:bg-rose-500/[0.12] hover:border-rose-500/40 hover:text-rose-300' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700'}`}
                              data-testid={`beta-countdown-preset-${p.weeks}w`}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className={`text-[10px] uppercase tracking-wider ${textSecondary} font-semibold mb-2`}>Custom date</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="datetime-local"
                            id="beta-countdown-custom-input"
                            data-testid="beta-countdown-custom-input"
                            className={`flex-1 min-w-[200px] px-3 py-2 rounded-lg text-sm border focus:outline-none focus:border-rose-400 ${isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                          />
                          <button
                            onClick={() => {
                              const input = document.getElementById('beta-countdown-custom-input');
                              const v = input?.value;
                              if (!v) {
                                toast.error('Pick a date first');
                                return;
                              }
                              handleSetBetaEndDate(new Date(v).toISOString());
                            }}
                            className="px-4 py-2 rounded-lg text-xs font-bold tracking-wider bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30 transition-colors"
                            data-testid="beta-countdown-custom-btn"
                          >
                            SET COUNTDOWN
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Signup Webhook Section */}
                <div className={`${cardClass} rounded-2xl overflow-hidden`}>
                  <div className="p-6 border-b border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-teal-400/10 flex items-center justify-center">
                          <Webhook size={20} className="text-teal-400" />
                        </div>
                        <div>
                          <h3 className={`text-lg font-semibold ${textPrimary}`}>New User Webhook</h3>
                          <p className={`text-sm ${textSecondary}`}>Fires when a user completes onboarding Step 1 or updates their plan</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const updated = { ...webhookSettings, signup_webhook_enabled: !webhookSettings.signup_webhook_enabled };
                          setWebhookSettings(updated);
                        }}
                        className={`px-4 py-2 rounded-lg text-xs font-bold tracking-wider border transition-all ${
                          webhookSettings.signup_webhook_enabled
                            ? 'bg-teal-500/20 text-teal-400 border-teal-500/50'
                            : `${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-100'} ${textSecondary} border-transparent`
                        }`}
                        data-testid="webhook-enable-toggle"
                      >
                        {webhookSettings.signup_webhook_enabled ? 'ENABLED' : 'DISABLED'}
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* Webhook URL Input */}
                    <div>
                      <label className={`block text-xs font-semibold ${textSecondary} mb-2 uppercase tracking-wider`}>Inbound Webhook URL</label>
                      <input
                        type="url"
                        value={webhookSettings.signup_webhook_url || ''}
                        onChange={(e) => setWebhookSettings({ ...webhookSettings, signup_webhook_url: e.target.value })}
                        placeholder="https://services.leadconnectorhq.com/hooks/..."
                        className={`w-full px-4 py-3 rounded-xl ${inputClass} text-sm focus:outline-none focus:border-teal-400/40`}
                        data-testid="webhook-url-input"
                      />
                    </div>

                    {/* Buttons Row */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={saveWebhookSettings}
                        disabled={savingWebhook}
                        className="px-6 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-all"
                        data-testid="save-webhook-settings-btn"
                      >
                        {savingWebhook ? 'Saving...' : 'Save Settings'}
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const res = await api.post('/api/admin/settings/webhooks/test');
                            if (res.data?.success) {
                              toast.success(`Test payload sent (HTTP ${res.data.status_code})`);
                            } else {
                              toast.error(res.data?.message || 'Test failed');
                            }
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Failed to send test');
                          }
                        }}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-all ${isDarkMode ? 'bg-white/[0.04] border-white/[0.08] text-white/70 hover:bg-white/[0.08] hover:text-white' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'}`}
                        data-testid="test-webhook-btn"
                      >
                        Send Test Payload
                      </button>
                    </div>

                    {/* Payload Preview */}
                    <div className={`rounded-xl border ${isDarkMode ? 'border-white/[0.06] bg-white/[0.02]' : 'border-gray-200 bg-gray-50'} overflow-hidden`}>
                      <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-white/[0.06]' : 'border-gray-200'} flex items-center gap-2`}>
                        <Info size={14} className="text-teal-400/60" />
                        <span className={`text-xs font-semibold ${textSecondary} uppercase tracking-wider`}>Webhook Payload Preview</span>
                      </div>
                      <pre className={`p-4 text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'} overflow-x-auto leading-relaxed`}>{`{
  "event": "user_onboarding_step1",
  "user": {
    "name": "John Smith",
    "email": "john@business.com",
    "plan": "agency",
    "business_name": "Smith Marketing",
    "business_website": "https://smithmarketing.com",
    "target_audience": "Real Estate",
    "created_at": "2026-04-05T12:00:00Z"
  }
}

// Also fires on plan change:
{
  "event": "plan_updated",
  "user": {
    "name": "John Smith",
    "email": "john@business.com",
    "plan": "pro",
    "plan_name": "Pro",
    ...
  }
}`}</pre>
                    </div>

                    {/* Setup Instructions */}
                    <div className={`rounded-xl border ${isDarkMode ? 'border-white/[0.06] bg-white/[0.02]' : 'border-gray-200 bg-gray-50'} p-5`}>
                      <h4 className={`text-sm font-semibold ${textPrimary} mb-3`}>Setup Instructions</h4>
                      <div className={`space-y-3 text-xs ${textSecondary} leading-relaxed`}>
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-400/10 text-teal-400 flex items-center justify-center text-[10px] font-bold">1</span>
                          <p>In LeadConnector, go to <strong className={textPrimary}>Automation &rarr; Workflows</strong> and create a new workflow</p>
                        </div>
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-400/10 text-teal-400 flex items-center justify-center text-[10px] font-bold">2</span>
                          <p>Add an <strong className={textPrimary}>Inbound Webhook</strong> trigger as the first step</p>
                        </div>
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-400/10 text-teal-400 flex items-center justify-center text-[10px] font-bold">3</span>
                          <p>Copy the webhook URL and paste it above. The URL typically looks like: <code className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-white/[0.06]' : 'bg-gray-200'}`}>https://services.leadconnectorhq.com/hooks/...</code></p>
                        </div>
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-400/10 text-teal-400 flex items-center justify-center text-[10px] font-bold">4</span>
                          <p>Add workflow actions to create/update contacts using the payload fields (name, email, business_name, plan, etc.)</p>
                        </div>
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-400/10 text-teal-400 flex items-center justify-center text-[10px] font-bold">5</span>
                          <p>Enable the toggle above and save. New user signups will automatically be sent to your CRM</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* FEEDBACK TAB — threaded inbox */}
          {activeTab === 'feedback' && (
            <motion.div key="feedback" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="feedback-tab">
              <div className={`${cardClass} rounded-2xl overflow-hidden`}>
                <div className={`p-6 border-b ${isDarkMode ? 'border-white/[0.05]' : 'border-gray-200'}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-teal-400/10 border border-teal-400/20 flex items-center justify-center">
                        <MessageCircle className="text-teal-400" size={20} />
                      </div>
                      <div>
                        <h3 className={`text-lg font-semibold ${textPrimary}`}>Feedback Inbox</h3>
                        <p className={`text-sm ${textSecondary}`}>
                          Grouped by user · {feedbackCounts.total} total
                          {feedbackCounts.unread > 0 && <> · <span className="text-rose-400 font-medium">{feedbackCounts.unread} unread</span></>}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 bg-white/[0.03] p-1 rounded-lg flex-wrap">
                      {[
                        { k: 'all',        label: 'All',        count: feedbackCounts.total },
                        { k: 'new',        label: 'New',        count: feedbackCounts.unread },
                        { k: 'read',       label: 'Read',       count: Math.max(0, feedbackCounts.total - feedbackCounts.unread) },
                        { k: 'favorites',  label: '★ Favorites', count: feedbackCounts.favorites },
                        { k: 'completed',  label: '✓ Completed', count: feedbackCounts.completed },
                      ].map((f) => (
                        <button
                          key={f.k}
                          onClick={() => setFeedbackFilter(f.k)}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            feedbackFilter === f.k
                              ? 'bg-teal-400/20 text-teal-300'
                              : `${textSecondary} hover:${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`
                          }`}
                          data-testid={`feedback-filter-${f.k}`}
                        >
                          {f.label}{f.count ? <span className="ml-1 opacity-70">({f.count})</span> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[400px]">
                  {/* Threads list */}
                  <div className={`lg:col-span-5 border-r ${isDarkMode ? 'border-white/[0.05]' : 'border-gray-200'} max-h-[70vh] overflow-y-auto`}>
                    {feedbackLoading ? (
                      <div className={`p-8 text-center ${textSecondary}`}>Loading…</div>
                    ) : feedbackThreads.length === 0 ? (
                      <div className="p-12 text-center">
                        <MessageCircle className={`mx-auto mb-3 ${textSecondary}`} size={36} />
                        <p className={`text-sm ${textSecondary}`}>No feedback in this view.</p>
                      </div>
                    ) : feedbackThreads.map((t) => {
                      const isSelected = selectedThreadEmail === t.user_email;
                      const hasUnread = t.unread_count > 0;
                      const hasFav = t.favorite_count > 0;
                      const allCompleted = t.completed_count === t.message_count && t.message_count > 0;
                      return (
                        <button
                          key={t.user_email}
                          onClick={() => openThread(t.user_email)}
                          className={`w-full text-left px-4 py-3 border-b flex items-start gap-3 transition-colors ${
                            isDarkMode ? 'border-white/[0.04]' : 'border-gray-100'
                          } ${isSelected ? (isDarkMode ? 'bg-white/[0.05]' : 'bg-gray-100') : `hover:${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'}`}`}
                          data-testid={`feedback-thread-${encodeURIComponent(t.user_email || 'unknown')}`}
                        >
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold ${
                            isDarkMode ? 'bg-teal-400/15 text-teal-300 border border-teal-400/25' : 'bg-teal-50 text-teal-700 border border-teal-200'
                          }`}>
                            {(t.user_name || t.user_email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <div className={`text-sm font-medium truncate ${textPrimary}`}>
                                {t.user_name || t.user_email || 'Anonymous'}
                                {t.message_count > 1 && (
                                  <span className={`ml-1.5 text-[11px] font-semibold ${textSecondary}`}>({t.message_count})</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {allCompleted && <Check size={12} className="text-emerald-400" />}
                                {hasFav && <Star size={12} className="text-amber-400 fill-amber-400" />}
                                {hasUnread && <span className="w-2 h-2 rounded-full bg-teal-400" aria-label="Unread" />}
                              </div>
                            </div>
                            <div className={`text-xs truncate ${textSecondary}`}>
                              {t.workspace_name || t.user_email || '—'} · {t.latest_created_at ? new Date(t.latest_created_at).toLocaleString() : ''}
                            </div>
                            <div className={`text-xs mt-1 line-clamp-2 ${textSecondary}`}>{t.preview}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Thread detail pane */}
                  <div className="lg:col-span-7 p-6 max-h-[70vh] overflow-y-auto">
                    {!selectedThreadEmail ? (
                      <div className={`h-full flex flex-col items-center justify-center text-center ${textSecondary}`}>
                        <MessageCircle size={36} className="mb-3 opacity-40" />
                        <p className="text-sm">Select a user to read their feedback thread</p>
                      </div>
                    ) : threadLoading ? (
                      <div className={`p-8 text-center ${textSecondary}`}>Loading thread…</div>
                    ) : (
                      <div data-testid="feedback-thread-detail" className="space-y-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className={`text-lg font-semibold ${textPrimary}`}>
                              {threadMessages[0]?.from_name || selectedThreadEmail}
                            </h4>
                            <div className={`text-xs ${textSecondary}`}>
                              {selectedThreadEmail} · {threadMessages.length} message{threadMessages.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <button
                            onClick={() => { setSelectedThreadEmail(null); setThreadMessages([]); }}
                            className={`text-xs ${textSecondary} hover:${textPrimary} px-3 py-1.5 rounded-md border ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}
                            data-testid="feedback-close-thread-btn"
                          >Close</button>
                        </div>

                        {threadMessages.map((m) => {
                          const CatIcon = m.category === 'bug' ? Bug : m.category === 'feature' ? Lightbulb : MessageCircle;
                          return (
                            <div
                              key={m.feedback_id}
                              className={`rounded-xl border ${isDarkMode ? 'border-white/[0.06] bg-white/[0.02]' : 'border-gray-200 bg-gray-50'} p-4 ${m.is_completed ? 'opacity-75' : ''}`}
                              data-testid={`feedback-message-${m.feedback_id}`}
                            >
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${
                                    m.category === 'bug' ? 'bg-rose-400/15 text-rose-300'
                                    : m.category === 'feature' ? 'bg-amber-400/15 text-amber-300'
                                    : m.category === 'other' ? 'bg-slate-400/15 text-slate-300'
                                    : 'bg-teal-400/15 text-teal-300'
                                  }`}>
                                    <CatIcon size={10} /> {m.category || 'general'}
                                  </span>
                                  {m.is_beta && (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wider border"
                                          style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f', borderColor: 'rgba(148,163,184,0.5)' }}>
                                      BETA
                                    </span>
                                  )}
                                  <span className={`text-[11px] ${textSecondary}`}>
                                    {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => toggleFavorite(m.feedback_id)}
                                    className={`p-1.5 rounded-md transition-colors ${m.is_favorite ? 'bg-amber-400/15' : `hover:${isDarkMode ? 'bg-white/5' : 'bg-gray-200'}`}`}
                                    title={m.is_favorite ? 'Unfavorite' : 'Favorite'}
                                    data-testid={`feedback-favorite-${m.feedback_id}`}
                                  >
                                    <Star size={14} className={m.is_favorite ? 'text-amber-400 fill-amber-400' : textSecondary} />
                                  </button>
                                  <button
                                    onClick={() => toggleComplete(m.feedback_id)}
                                    className={`p-1.5 rounded-md transition-colors ${m.is_completed ? 'bg-emerald-400/15' : `hover:${isDarkMode ? 'bg-white/5' : 'bg-gray-200'}`}`}
                                    title={m.is_completed ? 'Mark not complete' : 'Mark complete'}
                                    data-testid={`feedback-complete-${m.feedback_id}`}
                                  >
                                    <Check size={14} className={m.is_completed ? 'text-emerald-400' : textSecondary} />
                                  </button>
                                </div>
                              </div>

                              <div className={`whitespace-pre-wrap leading-relaxed text-[14px] ${textPrimary}`}>
                                {m.message}
                              </div>

                              {m.video_url && (
                                <a
                                  href={m.video_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`mt-3 flex items-center gap-2 p-2.5 rounded-lg border ${isDarkMode ? 'border-white/10 hover:border-teal-400/30 bg-white/[0.02]' : 'border-gray-200 hover:border-teal-400 bg-white'} transition-colors`}
                                >
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${
                                    m.video_provider === 'loom' ? 'from-violet-500 to-purple-500'
                                    : m.video_provider === 'wistia' ? 'from-sky-500 to-blue-600'
                                    : m.video_provider === 'youtube' ? 'from-red-500 to-rose-600'
                                    : m.video_provider === 'vimeo' ? 'from-cyan-500 to-teal-500'
                                    : 'from-slate-500 to-slate-600'
                                  } shadow-sm`}>
                                    <Play className="text-white" size={12} fill="currentColor" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <span className={`text-xs font-semibold ${textPrimary} capitalize`}>{m.video_provider || 'Video'} recording</span>
                                      <ExternalLink size={10} className={textSecondary} />
                                    </div>
                                    <div className={`text-[11px] ${textSecondary} truncate`}>{m.video_url}</div>
                                  </div>
                                </a>
                              )}

                              {m.screenshot && (
                                <button
                                  type="button"
                                  onClick={() => setLightboxSrc({ src: m.screenshot, id: m.feedback_id })}
                                  className={`mt-3 block w-full rounded-lg overflow-hidden border ${isDarkMode ? 'border-white/10 hover:border-teal-400/40' : 'border-gray-200 hover:border-teal-400'} bg-black/40 transition-colors`}
                                  title="Click to open full size"
                                  data-testid={`feedback-screenshot-${m.feedback_id}`}
                                >
                                  <img src={m.screenshot} alt="User screenshot" className="w-full max-h-[280px] object-contain" />
                                </button>
                              )}

                              {m.is_completed && m.completed_at && (
                                <div className={`mt-2 text-[10px] ${textSecondary}`}>
                                  Completed {new Date(m.completed_at).toLocaleString()} by {m.completed_by || 'admin'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Screenshot lightbox */}
              {lightboxSrc && (
                <div
                  className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
                  onClick={() => setLightboxSrc(null)}
                  data-testid="feedback-lightbox"
                >
                  <div
                    className="relative w-full max-w-6xl max-h-[92vh] bg-[#0d0f13] rounded-xl border border-white/10 overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                      <a
                        href={lightboxSrc.src}
                        download={`feedback-${lightboxSrc.id}.png`}
                        className="px-3 py-1.5 rounded-md text-xs bg-white/10 hover:bg-white/20 text-white inline-flex items-center gap-1.5 backdrop-blur"
                        data-testid="feedback-lightbox-download"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={12} /> Download
                      </a>
                      <button
                        onClick={() => setLightboxSrc(null)}
                        className="w-8 h-8 rounded-md bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center backdrop-blur"
                        aria-label="Close"
                        data-testid="feedback-lightbox-close"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <img
                      src={lightboxSrc.src}
                      alt="Feedback screenshot"
                      className="w-full h-auto max-h-[92vh] object-contain bg-black"
                    />
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ARCHIVED TAB */}
          {activeTab === 'archived' && (
            <motion.div key="archived" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className={`${cardClass} rounded-xl overflow-hidden`}>
                <div className={`p-5 border-b ${isDarkMode ? 'border-white/[0.05]' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-400/10 flex items-center justify-center">
                      <Archive className="text-gray-400" size={20} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${textPrimary}`}>Archived Workspaces</h3>
                      <p className={`text-sm ${textSecondary}`}>Archived workspaces can be restored at any time</p>
                    </div>
                  </div>
                </div>

                {loadingArchived ? (
                  <div className="p-8 text-center">
                    <RefreshCw size={20} className="animate-spin text-gray-400 mx-auto mb-2" />
                    <p className={`text-sm ${textSecondary}`}>Loading archived workspaces...</p>
                  </div>
                ) : archivedAgencies.length === 0 ? (
                  <div className="p-12 text-center">
                    <Archive size={32} className="text-gray-500 mx-auto mb-3" />
                    <p className={`text-sm ${textSecondary}`}>No archived workspaces</p>
                    <p className={`text-xs ${textSecondary} mt-1`}>When you archive a workspace, it will appear here</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/[0.05]">
                    {archivedAgencies.map((agency) => (
                      <div key={agency.id || agency.workspace_id} className={`p-4 flex items-center justify-between ${isDarkMode ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`} data-testid={`archived-agency-${agency.workspace_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div 
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-medium flex-shrink-0 opacity-50"
                            style={{ backgroundColor: agency.color || '#14b8a6' }}
                          >
                            {agency.name?.charAt(0) || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className={`font-medium ${textPrimary} truncate text-sm`}>{agency.name}</p>
                            <p className={`text-xs ${textSecondary} truncate`}>{agency.email}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPlanColor(agency.plan)} ml-2`}>
                            {agency.base_plan || agency.plan}
                          </span>
                          {agency.archived_at && (
                            <span className={`text-xs ${textSecondary} ml-2 hidden sm:inline`}>
                              Archived {new Date(agency.archived_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-4">
                          <button
                            onClick={() => handleRestoreAgency(agency.workspace_id || agency.id, agency.name)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-400/10 text-teal-400 hover:bg-teal-400/20 text-sm font-medium transition-colors"
                            data-testid={`restore-agency-${agency.workspace_id}`}
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                          <button
                            onClick={() => { setPermanentDeleteAgency(agency); setPermanentDeleteConfirm(''); }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 text-sm font-medium transition-colors"
                            data-testid={`permanent-delete-agency-${agency.workspace_id}`}
                            title="Permanently delete this workspace and all associated data"
                          >
                            <Trash2 size={14} /> Delete Forever
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
        </div>
        </div>

      {/* Permanent Delete Modal (archived workspaces) */}
      {permanentDeleteAgency && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4" data-testid="permanent-delete-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`w-full max-w-lg ${isDarkMode ? 'bg-[#1a1b1f]' : 'bg-white'} rounded-2xl border border-rose-500/30 p-7 shadow-2xl`}>
            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-rose-400" size={22} />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${textPrimary}`}>Permanently delete this workspace?</h3>
                <p className={`text-sm ${textSecondary} mt-1`}>
                  This cannot be undone. All associated data will be wiped from the system.
                </p>
              </div>
            </div>

            <div className={`rounded-xl border border-rose-500/20 bg-rose-500/5 p-4 mb-5`}>
              <div className="text-xs uppercase tracking-wider text-rose-400 mb-2">Will be deleted</div>
              <div className="space-y-1 text-sm">
                <div><span className={textSecondary}>Workspace:</span> <span className={`${textPrimary} font-medium`}>{permanentDeleteAgency.name}</span></div>
                <div><span className={textSecondary}>Owner email:</span> <span className={textPrimary}>{permanentDeleteAgency.email || '—'}</span></div>
                <div><span className={textSecondary}>ID:</span> <span className={`${textPrimary} font-mono text-xs`}>{permanentDeleteAgency.workspace_id}</span></div>
              </div>
              <ul className={`mt-3 text-xs ${textSecondary} list-disc list-inside space-y-0.5`}>
                <li>Agency + workspace records</li>
                <li>Sub-accounts, agents, calls, chat sessions</li>
                <li>Billing history, balances, integrations (LeadConnector, etc.)</li>
                <li>Owner user account &amp; sessions (if no other workspaces)</li>
              </ul>
            </div>

            <label className={`block text-xs font-semibold ${textSecondary} mb-1.5 uppercase tracking-wider`}>
              Type <span className="text-rose-400 font-bold">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={permanentDeleteConfirm}
              onChange={(e) => setPermanentDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none focus:ring-1 focus:ring-rose-400/60 font-mono tracking-widest text-center`}
              data-testid="permanent-delete-confirm-input"
            />

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => { setPermanentDeleteAgency(null); setPermanentDeleteConfirm(''); }}
                className={`flex-1 px-4 py-3 rounded-xl font-medium ${isDarkMode ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid="permanent-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePermanentDelete}
                disabled={permanentDeleting || permanentDeleteConfirm.trim().toUpperCase() !== 'DELETE'}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-rose-500 text-white font-semibold hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="permanent-delete-confirm-btn"
              >
                {permanentDeleting ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Trash2 size={16} />}
                Permanently Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Team Member Modal */}
      {showAddTeamModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`w-full max-w-md ${isDarkMode ? 'bg-[#1a1b1f]' : 'bg-white'} rounded-2xl p-6`}>
            <h3 className={`text-lg font-semibold ${textPrimary} mb-4`}>Add Team Member</h3>
            <form onSubmit={handleAddTeamMember} className="space-y-4">
              <input type="text" value={newTeamMember.name} onChange={(e) => setNewTeamMember({ ...newTeamMember, name: e.target.value })} placeholder="Name" className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none`} required />
              <input type="email" value={newTeamMember.email} onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })} placeholder="Email" className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none`} required />
              <select value={newTeamMember.role} onChange={(e) => setNewTeamMember({ ...newTeamMember, role: e.target.value })} className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none`}>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddTeamModal(false)} className={`flex-1 px-4 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button type="submit" className="flex-1 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-medium">Add</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Free Months Modal */}
      {showFreeMonthsModal && freeMonthsAgency && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`w-full max-w-md ${isDarkMode ? 'bg-[#1a1b1f]' : 'bg-white'} rounded-2xl p-6`}>
            <div className="flex items-center gap-3 mb-4">
              <Gift className="text-violet-400" size={24} />
              <div>
                <h3 className={`font-semibold ${textPrimary}`}>Free Months</h3>
                <p className={`text-sm ${textSecondary}`}>{freeMonthsAgency.name}</p>
              </div>
            </div>
            <form onSubmit={handleGrantFreeMonths} className="space-y-4">
              <select value={freeMonthsData.months} onChange={(e) => setFreeMonthsData({ ...freeMonthsData, months: parseInt(e.target.value) })} className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none`}>
                {[1, 2, 3, 6, 12].map(n => <option key={n} value={n}>{n} month{n > 1 ? 's' : ''}</option>)}
              </select>
              <div>
                <label className={`block text-sm ${textSecondary} mb-1`}>Resumes on</label>
                <input type="date" value={freeMonthsData.resume_date} onChange={(e) => setFreeMonthsData({ ...freeMonthsData, resume_date: e.target.value })} className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none`} />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowFreeMonthsModal(false)} className={`flex-1 px-4 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button type="submit" className="flex-1 px-4 py-3 rounded-xl bg-violet-500 text-white font-medium">Grant</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Settings Modal — rendered via Portal so it sits above everything (escapes <main> containing block) */}
      {showSettingsModal && settingsAgency && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4" data-testid="agency-settings-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowSettingsModal(false); setSettingsAgency(null); } }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`w-full max-w-3xl ${isDarkMode ? 'bg-[#1a1b1f]' : 'bg-white'} rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto`} data-testid="agency-settings-modal">
            <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'} sticky top-0 ${isDarkMode ? 'bg-[#1a1b1f]' : 'bg-white'} z-10`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-medium" style={{ backgroundColor: settingsAgency.color || '#14b8a6' }}>
                    {settingsAgency.name?.charAt(0)}
                  </div>
                  <div>
                    <h3 className={`font-semibold ${textPrimary}`}>Manage Agency</h3>
                    <p className={`text-xs ${textSecondary}`}>{settingsAgency.email}</p>
                  </div>
                </div>
                <button onClick={() => { setShowSettingsModal(false); setSettingsAgency(null); }} className={`p-2 rounded-lg ${textSecondary} hover:bg-white/5`} data-testid="agency-modal-close">
                  <X size={18} />
                </button>
              </div>
              {/* Tabs — switch between Settings / Pricing / Access without closing */}
              <div className="flex items-center gap-1 border-b border-white/[0.04] -mb-4">
                <button type="button" onClick={() => { const ag = settingsAgency; setShowSettingsModal(false); setSettingsAgency(null); setShowAgencyMenu(ag.email); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent flex items-center gap-1`} data-testid="tab-back-menu">
                  <ChevronLeft size={14} /> Menu
                </button>
                <button type="button" className={`px-3 py-2 text-sm border-b-2 ${textPrimary} border-teal-400`} data-testid="tab-settings">
                  Settings
                </button>
                <button type="button" onClick={() => { const ag = settingsAgency; setShowSettingsModal(false); setSettingsAgency(null); handleOpenAgencyPricing(ag); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent`} data-testid="tab-pricing">
                  Pricing
                </button>
                <button type="button" onClick={() => { const ag = settingsAgency; setShowSettingsModal(false); setSettingsAgency(null); handleOpenGrantAccess(ag); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent`} data-testid="tab-access">
                  Access
                </button>
              </div>
            </div>
            <form onSubmit={handleSaveSettings} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm ${textSecondary} mb-1.5`}>Business Name</label>
                <input type="text" value={settingsForm.name} onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })} className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none`} />
              </div>
              
              {/* Plan Selection */}
              <div>
                <label className={`block text-sm ${textSecondary} mb-1.5`}>Plan</label>
                <select 
                  value={settingsForm.plan} 
                  onChange={(e) => {
                    const newPlan = e.target.value;
                    const planData = planConfig?.plans?.find(p => p.id === newPlan);
                    setSettingsForm({ 
                      ...settingsForm, 
                      plan: newPlan,
                      subAccountLimit: planData?.subAccounts || 3,
                      customMrr: settingsForm.useCustomMrr ? settingsForm.customMrr : ''
                    });
                  }} 
                  className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none`}
                >
                  {planConfig?.plans?.map(p => (
                    <option key={p.id} value={p.id}>{p.name} - ${p.monthly}/mo</option>
                  )) || (
                    <>
                      <option value="launchpad">Free - $0/mo</option>
                      <option value="starter">Starter - $197/mo</option>
                      <option value="agency">Agency - $397/mo</option>
                      <option value="pro">Pro - $897/mo</option>
                    </>
                  )}
                </select>
              </div>

              {/* Lifetime Toggle */}
              <label className={`md:col-span-2 flex items-center gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-amber-400/5 border border-amber-400/20' : 'bg-amber-50 border border-amber-200'} cursor-pointer`}>
                <input type="checkbox" checked={settingsForm.isLifetime} onChange={(e) => setSettingsForm({ ...settingsForm, isLifetime: e.target.checked })} className="w-4 h-4 rounded" />
                <span className={`flex items-center gap-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                  <Infinity size={14} /> Lifetime Access (No billing)
                </span>
              </label>

              {/* Unlimited Balance Toggle */}
              <label className={`md:col-span-2 flex items-center gap-3 p-3 rounded-xl ${
                settingsForm.unlimitedCredits
                  ? isDarkMode ? 'bg-teal-400/10 border border-teal-400/30' : 'bg-teal-50 border border-teal-300'
                  : isDarkMode ? 'bg-white/[0.02] border border-white/[0.06]' : 'bg-gray-50 border border-gray-200'
              } cursor-pointer`} data-testid="unlimited-credits-toggle">
                <input 
                  type="checkbox" 
                  checked={settingsForm.unlimitedCredits} 
                  onChange={(e) => setSettingsForm({ ...settingsForm, unlimitedCredits: e.target.checked })} 
                  className="w-4 h-4 rounded"
                />
                <div className="flex-1">
                  <span className={`flex items-center gap-1.5 text-sm font-medium ${settingsForm.unlimitedCredits ? (isDarkMode ? 'text-teal-400' : 'text-teal-700') : textPrimary}`}>
                    <Infinity size={14} /> Unlimited Balance
                  </span>
                  <p className={`text-xs mt-0.5 ${textSecondary}`}>
                    Platform usage — no balance top-ups needed. Usage is still tracked for cost reporting.
                  </p>
                </div>
              </label>

              {/* Rate Tier Toggle */}
              <div className={`md:col-span-2 p-4 rounded-xl ${isDarkMode ? 'bg-white/[0.02] border border-white/[0.05]' : 'bg-gray-50 border border-gray-200'}`} data-testid="rate-tier-toggle">
                <label className={`block text-sm font-medium ${textPrimary} mb-2`}>Billing Tier</label>
                <p className={`text-xs ${textSecondary} mb-3`}>Controls what this agency pays per service usage.</p>
                <div className="flex gap-2">
                  {[
                    { value: 'standard', label: 'Markup', desc: 'Retail pricing (with margin)', color: 'teal' },
                    { value: 'cost', label: 'Cost', desc: 'At-cost (no markup)', color: 'amber' }
                  ].map(tier => (
                    <button
                      key={tier.value}
                      type="button"
                      onClick={() => setSettingsForm({ ...settingsForm, rateTier: tier.value })}
                      className={`flex-1 p-3 rounded-xl border text-left transition-all ${
                        settingsForm.rateTier === tier.value
                          ? tier.value === 'cost'
                            ? isDarkMode ? 'bg-amber-400/10 border-amber-400/40 ring-1 ring-amber-400/20' : 'bg-amber-50 border-amber-400 ring-1 ring-amber-200'
                            : isDarkMode ? 'bg-teal-400/10 border-teal-400/40 ring-1 ring-teal-400/20' : 'bg-teal-50 border-teal-400 ring-1 ring-teal-200'
                          : isDarkMode ? 'border-white/[0.08] hover:border-white/20' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`rate-tier-${tier.value}`}
                    >
                      <span className={`text-sm font-semibold block ${
                        settingsForm.rateTier === tier.value
                          ? tier.value === 'cost' ? (isDarkMode ? 'text-amber-400' : 'text-amber-700') : (isDarkMode ? 'text-teal-400' : 'text-teal-700')
                          : textPrimary
                      }`}>{tier.label}</span>
                      <span className={`text-[11px] ${textSecondary}`}>{tier.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sub-account Limit (plan-based) */}
              <div data-testid="admin-sub-account-limit-info">
                <label className={`block text-sm ${textSecondary} mb-1.5`}>Sub-Account Limit</label>
                <p className={`text-sm ${textPrimary}`}>
                  {settingsForm.isLifetime ? 'Unlimited' : ({ launchpad: 1, starter: 3, agency: 10 }[settingsForm.plan] ?? 'Unlimited')} — set by plan
                </p>
                <p className={`text-xs ${textSecondary} mt-1`}>
                  Current usage: {settingsAgency.sub_accounts || 0} sub-accounts
                </p>
              </div>

              {/* Password Reset */}
              <div>
                <label className={`block text-sm ${textSecondary} mb-1.5`}>Reset Password</label>
                <input type="password" value={settingsForm.newPassword} onChange={(e) => setSettingsForm({ ...settingsForm, newPassword: e.target.value })} placeholder="Leave blank to keep current" className={`w-full px-4 py-3 rounded-xl ${inputClass} border focus:outline-none`} />
              </div>

              {/* Workspace ID */}
              <div className={`md:col-span-2 p-3 rounded-xl ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                <p className={`text-xs ${textSecondary} mb-1`}>Workspace ID</p>
                <div className="flex items-center gap-2">
                  <code className={`text-sm ${textPrimary} font-mono`}>{settingsAgency.workspace_id}</code>
                  <button type="button" onClick={() => copyToClipboard(settingsAgency.workspace_id)} className="p-1 hover:bg-white/10 rounded">
                    <Copy size={12} className="text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Change Owner Email — updates login identity + emails a fresh temp password */}
              <div className={`md:col-span-2 p-4 rounded-xl border ${isDarkMode ? 'bg-amber-400/[0.04] border-amber-400/20' : 'bg-amber-50 border-amber-200'}`} data-testid="change-owner-email-card">
                <label className={`block text-sm font-medium ${textPrimary} mb-1`}>Change Owner Email</label>
                <p className={`text-xs ${textSecondary} mb-3`}>
                  Updates the owner's login email and emails them a new temporary password (they'll be asked to reset it on next login). Current: <span className={textPrimary}>{settingsAgency.email}</span>
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    value={changeEmailValue}
                    onChange={(e) => setChangeEmailValue(e.target.value)}
                    placeholder="new-owner@example.com"
                    className={`flex-1 px-4 py-2.5 rounded-xl ${inputClass} border focus:outline-none focus:ring-1 focus:ring-amber-400/50 text-sm`}
                    data-testid="change-email-input"
                  />
                  <button
                    type="button"
                    onClick={handleChangeAgencyEmail}
                    disabled={changingEmail || !changeEmailValue.trim()}
                    className="px-4 py-2.5 rounded-xl bg-amber-400 text-[#0a0b0f] font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                    data-testid="change-email-submit-btn"
                  >
                    {changingEmail ? <RefreshCw size={14} className="animate-spin" /> : <Mail size={14} />}
                    Change &amp; Send Password
                  </button>
                </div>
                {changeEmailResult && (
                  <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-white'} border ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`} data-testid="change-email-result">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                      Email updated to {changeEmailResult.new_email}
                    </p>
                    <div className={`flex items-center gap-2 mt-2 p-2 rounded ${isDarkMode ? 'bg-black/40' : 'bg-gray-100'}`}>
                      <span className={`text-xs ${textSecondary}`}>Temp Password:</span>
                      <code className={`text-sm font-mono ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>{changeEmailResult.temp_password}</code>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(changeEmailResult.temp_password); toast.success('Copied'); }}
                        className={`p-1 rounded hover:bg-white/10 ${textSecondary}`}
                      >
                        <Copy size={12} />
                      </button>
                    </div>
                    <p className={`text-xs ${textSecondary} mt-2`}>
                      {changeEmailResult.email_sent
                        ? 'Temporary password emailed to the new address.'
                        : 'Email could not be sent — share this temporary password manually.'}
                    </p>
                  </div>
                )}
              </div>

              <button type="button" onClick={handleResendInvite} className={`md:col-span-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl ${isDarkMode ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                <Mail size={16} /> Resend Invite
              </button>

              <div className="md:col-span-2 flex gap-3 pt-2 sticky bottom-0 bg-inherit">
                <button type="button" onClick={() => { setShowSettingsModal(false); setSettingsAgency(null); }} className={`flex-1 px-4 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button type="submit" className="flex-1 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-medium">Save Changes</button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Agency Custom Pricing Modal */}
      {showAgencyPricingModal && pricingAgency && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowAgencyPricingModal(false); }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className={`w-full max-w-2xl ${isDarkMode ? 'bg-[#1a1b1f]' : 'bg-white'} rounded-2xl p-6 max-h-[90vh] overflow-y-auto`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-medium" style={{ backgroundColor: pricingAgency.color || '#14b8a6' }}>
                  {pricingAgency.name?.charAt(0)}
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Manage Agency</h3>
                  <p className={`text-xs ${textSecondary}`}>{pricingAgency.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPlanColor(pricingAgency.plan)}`}>
                  {pricingAgency.base_plan || pricingAgency.plan}
                </span>
                <button type="button" onClick={() => setShowAgencyPricingModal(false)} className={`p-2 rounded-lg ${textSecondary} hover:bg-white/5`}>
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-white/[0.04] mb-5">
              <button type="button" onClick={() => { const ag = pricingAgency; setShowAgencyPricingModal(false); setShowAgencyMenu(ag.email); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent flex items-center gap-1`} data-testid="pricing-tab-back-menu">
                <ChevronLeft size={14} /> Menu
              </button>
              <button type="button" onClick={() => { const ag = pricingAgency; setShowAgencyPricingModal(false); handleOpenSettings(ag); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent`} data-testid="pricing-tab-settings">
                Settings
              </button>
              <button type="button" className={`px-3 py-2 text-sm ${textPrimary} border-b-2 border-teal-400`} data-testid="pricing-tab-pricing">
                Pricing
              </button>
              <button type="button" onClick={() => { const ag = pricingAgency; setShowAgencyPricingModal(false); handleOpenGrantAccess(ag); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent`} data-testid="pricing-tab-access">
                Access
              </button>
            </div>

            <form onSubmit={handleSaveAgencyPricing} className="space-y-4">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                <p className={`text-xs ${textSecondary} mb-3`}>Leave blank to use plan defaults</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs ${textSecondary} mb-1`}>Voice Rate</label>
                    <div className="relative">
                      <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${textSecondary}`}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={agencyPricingForm.custom_voice_rate}
                        onChange={(e) => setAgencyPricingForm({...agencyPricingForm, custom_voice_rate: e.target.value})}
                        placeholder="Plan default"
                        className={`w-full pl-5 pr-8 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                      />
                      <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${textSecondary}`}>/min</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-xs ${textSecondary} mb-1`}>Phone Fee</label>
                    <div className="relative">
                      <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${textSecondary}`}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={agencyPricingForm.custom_phone_fee}
                        onChange={(e) => setAgencyPricingForm({...agencyPricingForm, custom_phone_fee: e.target.value})}
                        placeholder="Plan default"
                        className={`w-full pl-5 pr-8 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                      />
                      <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${textSecondary}`}>/mo</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-xs ${textSecondary} mb-1`}>SMS Rate</label>
                    <div className="relative">
                      <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${textSecondary}`}>$</span>
                      <input
                        type="number"
                        step="0.001"
                        value={agencyPricingForm.custom_sms_rate}
                        onChange={(e) => setAgencyPricingForm({...agencyPricingForm, custom_sms_rate: e.target.value})}
                        placeholder="Plan default"
                        className={`w-full pl-5 pr-2 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-xs ${textSecondary} mb-1`}>Included Minutes</label>
                    <input
                      type="number"
                      value={agencyPricingForm.custom_included_minutes}
                      onChange={(e) => setAgencyPricingForm({...agencyPricingForm, custom_included_minutes: e.target.value})}
                      placeholder="Plan default"
                      className={`w-full px-2 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm`}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className={`block text-xs ${textSecondary} mb-1`}>Notes (internal)</label>
                <textarea
                  value={agencyPricingForm.notes}
                  onChange={(e) => setAgencyPricingForm({...agencyPricingForm, notes: e.target.value})}
                  placeholder="Why this agency has custom pricing..."
                  rows={2}
                  className={`w-full px-3 py-2 rounded-lg ${inputClass} border focus:outline-none text-sm resize-none`}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleResetAgencyPricing}
                  className={`px-4 py-2.5 rounded-xl text-red-400 hover:bg-red-400/10 text-sm`}
                >
                  Reset to Default
                </button>
                <div className="flex-1" />
                <button 
                  type="button" 
                  onClick={() => setShowAgencyPricingModal(false)} 
                  className={`px-4 py-2.5 rounded-xl ${isDarkMode ? 'bg-white/5 text-white' : 'bg-gray-100 text-gray-700'} text-sm`}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={savingAgencyPricing}
                  className="px-4 py-2.5 rounded-xl bg-teal-400 text-[#0a0b0f] font-medium text-sm disabled:opacity-50"
                >
                  {savingAgencyPricing ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      {/* GRANT ACCESS MODAL */}
      {showGrantAccessModal && grantAccessAgency && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowGrantAccessModal(false); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`w-full max-w-2xl ${cardClass} rounded-2xl p-6 border ${isDarkMode ? 'border-white/10' : 'border-gray-200'} max-h-[90vh] overflow-y-auto`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-medium" style={{ backgroundColor: grantAccessAgency.color || '#14b8a6' }}>
                  {grantAccessAgency.name?.charAt(0)}
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Manage Agency</h3>
                  <p className={`text-xs ${textSecondary}`}>{grantAccessAgency.email}</p>
                </div>
              </div>
              <button onClick={() => setShowGrantAccessModal(false)} className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                <X size={18} className={textSecondary} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-white/[0.04] mb-5">
              <button type="button" onClick={() => { const ag = grantAccessAgency; setShowGrantAccessModal(false); setShowAgencyMenu(ag.email); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent flex items-center gap-1`} data-testid="access-tab-back-menu">
                <ChevronLeft size={14} /> Menu
              </button>
              <button type="button" onClick={() => { const ag = grantAccessAgency; setShowGrantAccessModal(false); handleOpenSettings(ag); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent`} data-testid="access-tab-settings">
                Settings
              </button>
              <button type="button" onClick={() => { const ag = grantAccessAgency; setShowGrantAccessModal(false); handleOpenAgencyPricing(ag); }} className={`px-3 py-2 text-sm ${textSecondary} hover:${textPrimary} border-b-2 border-transparent`} data-testid="access-tab-pricing">
                Pricing
              </button>
              <button type="button" className={`px-3 py-2 text-sm ${textPrimary} border-b-2 border-teal-400`} data-testid="access-tab-access">
                Access
              </button>
            </div>

            {grantAccessResult && (
              <div className={`mb-4 p-4 rounded-lg border ${
                grantAccessResult.is_new_user 
                  ? (isDarkMode ? 'bg-teal-400/5 border-teal-400/20' : 'bg-teal-50 border-teal-200')
                  : (isDarkMode ? 'bg-blue-400/5 border-blue-400/20' : 'bg-blue-50 border-blue-200')
              }`} data-testid="admin-grant-result">
                {grantAccessResult.is_new_user && grantAccessResult.temp_password ? (
                  <div>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                      New account created
                    </p>
                    <div className={`flex items-center gap-2 mt-2 p-2 rounded ${isDarkMode ? 'bg-black/30' : 'bg-gray-100'}`}>
                      <span className={`text-xs ${textSecondary}`}>Temp Password:</span>
                      <code className={`text-sm font-mono ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>{grantAccessResult.temp_password}</code>
                      <button
                        onClick={() => { navigator.clipboard.writeText(grantAccessResult.temp_password); toast.success('Copied'); }}
                        className={`p-1 rounded hover:bg-white/10 ${textSecondary}`}
                      >
                        <Copy size={12} />
                      </button>
                    </div>
                    <p className={`text-xs ${textSecondary} mt-2`}>User will be prompted to change password on first login.</p>
                  </div>
                ) : (
                  <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    Access granted to existing user.
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleGrantAccess} className="space-y-4">
              <div>
                <label className={`block text-sm ${textSecondary} mb-1.5`}>Email Address</label>
                <input
                  type="email"
                  value={grantAccessEmail}
                  onChange={(e) => setGrantAccessEmail(e.target.value)}
                  placeholder="user@example.com"
                  className={`w-full px-4 py-2.5 rounded-xl ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50 text-sm`}
                  required
                  data-testid="admin-grant-email-input"
                />
              </div>
              <div>
                <label className={`block text-sm ${textSecondary} mb-1.5`}>Permission Level</label>
                <select
                  value={grantAccessPermission}
                  onChange={(e) => setGrantAccessPermission(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl ${inputClass} border focus:outline-none text-sm`}
                  data-testid="admin-grant-permission-select"
                >
                  <option value="viewer">Viewer — Read-only access</option>
                  <option value="moderator">Moderator — Manage sub-accounts</option>
                  <option value="admin">Admin — Full workspace control</option>
                </select>
              </div>
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'} text-xs ${textSecondary}`}>
                New users get a temporary password via email. Existing users get instant access.
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowGrantAccessModal(false)}
                  className={`flex-1 px-4 py-2.5 rounded-xl ${isDarkMode ? 'bg-white/5 text-white' : 'bg-gray-100 text-gray-700'} text-sm`}
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={isGrantingAccess || !grantAccessEmail}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-teal-400 text-[#0a0b0f] font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="admin-grant-submit-btn"
                >
                  {isGrantingAccess ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <UserPlus size={14} />
                  )}
                  Grant Access
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmAgency && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { if (!isDeleting) { setDeleteConfirmAgency(null); setDeleteConfirmText(''); } }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`${isDarkMode ? 'bg-[#1a1b1f]' : 'bg-white'} rounded-2xl p-6 w-full max-w-md mx-4 border ${isDarkMode ? 'border-white/10' : 'border-gray-200'} shadow-2xl`}
            onClick={e => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Archive size={20} className="text-red-400" />
              </div>
              <h3 className={`text-lg font-semibold ${textPrimary}`}>Archive Workspace</h3>
            </div>
            <p className={`${textSecondary} mb-2`}>
              Are you sure you want to archive <span className={`font-semibold ${textPrimary}`}>{deleteConfirmAgency.name}</span>?
            </p>
            <p className={`${textSecondary} text-sm mb-4`}>
              This workspace and all its sub-accounts will be deactivated. You can restore it later from the Archived section.
            </p>
            <div className="mb-5">
              <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                Type <span className="text-red-400 font-mono font-bold">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE here"
                className={`w-full px-4 py-2.5 rounded-xl ${inputClass} border focus:outline-none focus:ring-1 ${deleteConfirmText === 'DELETE' ? 'focus:ring-red-400/50 border-red-400/30' : 'focus:ring-white/20'} text-sm font-mono`}
                autoFocus
                data-testid="delete-confirm-input"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setDeleteConfirmAgency(null); setDeleteConfirmText(''); }}
                disabled={isDeleting}
                className={`px-4 py-2 rounded-lg text-sm ${textSecondary} hover:bg-white/5 transition-colors`}
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                className="px-4 py-2 rounded-lg text-sm bg-red-500 hover:bg-red-600 text-white font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="delete-confirm-btn"
              >
                {isDeleting ? 'Archiving...' : 'Archive Workspace'}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

    </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
